<template>
	<v-row v-if="!$store.getters.progress" class="ma-0 pb-offset">
		<v-col class="pa-0">
			<!-- Project Info -->
			<show v-if="projectModels.project"
				:id="id"
				:models="projectModels"
				:infos="infos"
				topClass="mb-0"
			></show>

			<!-- KPI Section -->
			<v-row dense class="ma-0">
				<v-col cols="12" sm="6" lg="3" class="px-2 pl-lg- d-flex pl-md-0 pb-md-2 pb-sm-2 pb-2 pt-sm-1">
					<v-hover v-slot:default="{ hover }" class="flex-grow-1">
						<v-card :elevation="hover ? 4 : 2" class="background-2 round-card fill-height">
							<v-card-text class="pa-0">
								<div class="pa-3 d-flex align-center">
									<v-icon color="success" class="mr-2">mdi-trending-up</v-icon>
									<span class="flex-grow-1">Ricavi ad oggi</span>
									<commission-kpi-customize
										v-if="availableRevenueTypes.length > 0"
										v-model="selectedRevenueTypes"
										:items="availableRevenueTypes"
										:defaults="defaultRevenueTypes"
										:is-default="revenueIsDefault"
										title="Ricavi da sommare"
									></commission-kpi-customize>
								</div>
								<div class="d-flex flex-column align-center pb-4">
									<span class="text-h4 success--text font-weight-medium">{{ kpiRevenue | currency }}</span>
									<!-- Calcolo automatico (forecast.today), oppure selezione personalizzata dichiarata -->
									<span v-if="revenueIsDefault" class="caption text--secondary d-flex align-center">
										calcolo automatico
										<help-hint :text="revenueAutoHint" class="ml-1"></help-hint>
									</span>
									<template v-else>
										<span class="caption warning--text px-3 text-center">personalizzato<template v-if="revenueSelectionWarning">: {{ revenueSelectionWarning }}</template></span>
										<v-btn x-small text color="primary" class="text-none" @click="selectedRevenueTypes = defaultRevenueTypes.slice()">
											<v-icon x-small left>mdi-restore</v-icon>
											Torna al calcolo automatico
										</v-btn>
									</template>
								</div>
							</v-card-text>
						</v-card>
					</v-hover>
				</v-col>

				<v-col cols="12" sm="6" lg="3" class="px-2 d-flex px-lg-2 pr-md-0 pb-md-2 pb-sm-2 pt-2 pb-2 pt-sm-1">
					<v-hover v-slot:default="{ hover }" class="flex-grow-1">
						<v-card :elevation="hover ? 4 : 2" class="background-2 round-card fill-height">
							<v-card-text class="pa-0">
								<div class="pa-3 d-flex align-center">
									<v-icon color="error" class="mr-2">mdi-trending-down</v-icon>
									<span class="flex-grow-1">Costi ad oggi</span>
									<commission-kpi-customize
										v-if="availableCostTypes.length > 0"
										v-model="selectedCostTypes"
										:items="availableCostTypes"
										:defaults="defaultCostTypes"
										:is-default="costIsDefault"
										title="Costi da sommare"
									></commission-kpi-customize>
								</div>
								<div class="d-flex flex-column align-center pb-4">
									<span class="text-h4 error--text font-weight-medium">{{ kpiCost | currency }}</span>
									<span v-if="costIsDefault" class="caption text--secondary d-flex align-center">
										calcolo automatico
										<help-hint :text="costAutoHint" class="ml-1"></help-hint>
									</span>
									<template v-else>
										<span class="caption warning--text px-3 text-center">personalizzato<template v-if="costSelectionWarning">: {{ costSelectionWarning }}</template></span>
										<v-btn x-small text color="primary" class="text-none" @click="selectedCostTypes = defaultCostTypes.slice()">
											<v-icon x-small left>mdi-restore</v-icon>
											Torna al calcolo automatico
										</v-btn>
									</template>
								</div>
							</v-card-text>
						</v-card>
					</v-hover>
				</v-col>

				<v-col cols="12" sm="6" lg="3" class="px-2 d-flex px-lg-2 pt-lg-1 pl-md-0 pt-md-2 pt-sm-2 pb-2 pt-2">
					<v-hover v-slot:default="{ hover }" class="flex-grow-1">
						<v-card :elevation="hover ? 4 : 2" class="background-2 round-card fill-height">
							<v-card-text class="pa-0">
								<div class="pa-3 d-flex align-center">
									<v-icon :color="kpiMargin >= 0 ? 'success' : 'error'" class="mr-2">mdi-currency-eur</v-icon>
									<span>Margine ad oggi</span>
								</div>
								<div class="d-flex flex-column align-center pb-4">
									<span class="text-h4 font-weight-medium" :class="kpiMargin >= 0 ? 'success--text' : 'error--text'">
										{{ kpiMargin | currency }}
									</span>
									<span class="caption text--secondary d-flex align-center">
										<span v-if="calculatedMarginPercentage !== null">({{ formatPercentage(calculatedMarginPercentage) }})</span>
										<help-hint v-else :text="`Percentuale non significativa: i ricavi ad oggi sono ${$options.filters.currency(kpiRevenue)}. Si calcola solo con ricavi positivi.`">
											<template v-slot:activator="{ on, attrs }">
												<span class="text-decoration-underline" style="cursor: pointer" v-bind="attrs" v-on="on">(n.s.)</span>
											</template>
										</help-hint>
										<span v-if="!revenueIsDefault || !costIsDefault" class="warning--text ml-1">personalizzato</span>
									</span>
								</div>
							</v-card-text>
						</v-card>
					</v-hover>
				</v-col>

				<v-col cols="12" sm="6" lg="3" class="px-2 d-flex pr-lg-0 pt-lg-1 pb-lg-2 pr-md-0 pt-md-2 pt-sm-2 pt-2">
					<v-hover v-slot:default="{ hover }" class="flex-grow-1">
						<v-card :elevation="hover ? 4 : 2" class="background-2 round-card fill-height">
							<v-card-text class="pa-0">
								<div class="pa-3 d-flex align-center">
									<v-icon color="info" class="mr-2">mdi-file-document-multiple</v-icon>
									<span>Documenti Totali</span>
								</div>
								<div class="d-flex flex-column align-center pb-4">
									<span class="text-h4 info--text font-weight-medium">{{ totalDocumentsCount }}</span>
									<span class="caption text--secondary">&nbsp;</span>
								</div>
							</v-card-text>
						</v-card>
					</v-hover>
				</v-col>
			</v-row>

			<!-- Ordini di vendita mai evasi mentre la commessa ha gia' DDT: di solito DDT fatti a mano -->
			<v-row v-if="unprocessedSaleorders.length > 0" class="ma-0">
				<v-col cols="12" class="px-2 pt-0 pb-2">
					<v-alert type="warning" text dense class="mb-0">
						{{ unprocessedSaleorders.length === 1 ? 'L\'ordine di vendita' : 'Gli ordini di vendita' }}
						<template v-for="(order, index) in unprocessedSaleorders">
							<router-link :key="order.id" :to="documentUrl('saleorders', order.id)" class="text-decoration-underline">{{ order.number }}</router-link>{{ index !== unprocessedSaleorders.length - 1 ? ', ' : '' }}
						</template>
						{{ unprocessedSaleorders.length === 1 ? 'risulta' : 'risultano' }} ancora da evadere, ma la commessa ha già {{ statistics.warnings.unprocessed_saleorders.ddts }} DDT di vendita.
						Se i DDT sono stati fatti a mano l'ordine continua a impegnare la giacenza: crea i DDT dall'evasione dell'ordine, oppure segna l'ordine come evaso.
					</v-alert>
				</v-col>
			</v-row>

			<!-- Previsto e consuntivo (statistics.forecast, Project::commissionForecast): sempre visibile, non dipende
				dalle voci scelte nei KPI. Su mobile la tabella passa al layout a schede di v-data-table -->
			<v-row v-if="forecastRows.length > 0" class="ma-0">
				<v-col cols="12" class="px-2 pt-0 pb-2">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="primary">mdi-chart-box-outline</v-icon>
							Previsto e consuntivo
							<!-- Aiuto corto con un esempio: le regole complete sono nel dialog -->
							<help-hint small class="ml-1" max-width="340" label="Come si legge">
								<template v-slot:default="{ close }">
									Previsto è il contratto con i suoi costi stimati; Ad oggi conta il più alto tra consegnato e fatturato e i costi già sostenuti.
									Es.: contratto 1.000 €, consegnato 600 €, fatturato 400 € → ricavi ad oggi 600 €, da fatturare 600 €.
									<div class="mt-2"><a class="text-decoration-underline" @click="close(); rulesDialog = true">Tutte le regole di calcolo</a></div>
								</template>
							</help-hint>
						</v-card-title>
						<v-divider></v-divider>
						<!-- Lettura a costi (forecast.progress): avanzamento, fatturato rispetto all'avanzamento, margini -->
						<v-card-text v-if="forecastProgress" class="pb-0">
							<v-row dense>
								<v-col cols="12" md="4">
									<help-hint :text="forecastProgress.progressHint" max-width="340">
										<template v-slot:activator="{ on, attrs }">
											<div style="width: 100%; cursor: pointer" v-bind="attrs" v-on="on">
												<div class="d-flex align-center mb-1">
													<span class="caption font-weight-medium">Avanzamento (stimato sui costi)</span>
													<span class="font-weight-bold ml-2">{{ forecastProgress.unreliable ? 'n.s.' : formatPercentage(forecastProgress.percentage) }}</span>
												</div>
												<!-- pista a tutta larghezza: con buffer-value Vuetify disegna lo sfondo solo fino all'ordinato -->
												<div class="grey lighten-3" style="border-radius: 5px; overflow: hidden">
													<v-progress-linear
														:value="forecastProgress.percentage === null ? 0 : Math.min(forecastProgress.percentage, 100)"
														:buffer-value="forecastProgress.committedPercentage === null ? 100 : Math.min(forecastProgress.committedPercentage, 100)"
														:color="forecastProgress.progressColor"
														height="10"
														rounded
													></v-progress-linear>
												</div>
												<div class="caption text--secondary mt-1">
													<template v-if="forecastProgress.percentage !== null">
														Contando gli ordini ai fornitori {{ formatPercentage(forecastProgress.committedPercentage) }}<br>
														Costo atteso a fine lavori {{ forecastProgress.estimate | currency }}
													</template>
													<template v-else-if="forecastProgress.unreliable">Costo previsto non affidabile</template>
													<template v-else>Nessun costo previsto</template>
												</div>
												<!-- Il costo atteso supera il previsto (forecast.cost.over_planned): arancione, rosso solo a progetto chiuso -->
												<div v-if="forecastProgress.overPlanned > 0" class="caption font-weight-medium" :class="forecastProgress.overPlannedColor + '--text'">
													<v-icon x-small :color="forecastProgress.overPlannedColor" class="mr-1">mdi-alert-outline</v-icon>Oltre il previsto di {{ forecastProgress.overPlanned | currency }}
												</div>
											</div>
										</template>
									</help-hint>
								</v-col>
								<v-col cols="12" md="4">
									<help-hint :text="forecastProgress.billingHint" max-width="340">
										<template v-slot:activator="{ on, attrs }">
											<div style="width: 100%; cursor: pointer" v-bind="attrs" v-on="on">
												<div class="d-flex align-center mb-1">
													<span class="caption font-weight-medium">Fatturato rispetto all'avanzamento</span>
													<span class="font-weight-bold ml-2">{{ forecastProgress.unreliable ? 'n.s.' : formatPercentage(forecastProgress.billingPercentage) }}</span>
												</div>
												<v-progress-linear
													:value="forecastProgress.billingPercentage === null ? 0 : Math.min(forecastProgress.billingPercentage, 100)"
													:color="forecastProgress.billingColor"
													height="10"
													rounded
												></v-progress-linear>
												<div class="caption mt-1" :class="forecastProgress.billingTextClass">
													{{ forecastProgress.billingLabel }}
												</div>
											</div>
										</template>
									</help-hint>
								</v-col>
								<v-col cols="12" md="4">
									<!-- i due margini affiancati e centrati nella colonna, come le barre a fianco -->
									<div class="d-flex flex-wrap justify-space-around align-center text-center fill-height">
										<help-hint text="Ricavi ad oggi − costi ad oggi: è lo stesso valore del KPI Margine ad oggi in alto.">
											<template v-slot:activator="{ on, attrs }">
												<div class="mb-1 px-2" style="cursor: pointer" v-bind="attrs" v-on="on">
													<div class="caption text--secondary mb-1">Margine ad oggi</div>
													<span class="subtitle-1 font-weight-bold" :class="statistics.forecast.margin.actual >= 0 ? 'success--text' : 'error--text'">{{ statistics.forecast.margin.actual | currency }}</span>
												</div>
											</template>
										</help-hint>
										<help-hint :text="forecastProgress.marginHint" max-width="340">
											<template v-slot:activator="{ on, attrs }">
												<div class="mb-1 px-2" style="cursor: pointer" v-bind="attrs" v-on="on">
													<div class="caption text--secondary mb-1">Margine atteso a fine lavori</div>
													<template v-if="forecastProgress.marginAtCompletion !== null">
														<span class="subtitle-1 font-weight-bold" :class="forecastProgress.marginAtCompletion >= 0 ? 'success--text' : 'error--text'">{{ forecastProgress.marginAtCompletion | currency }}</span>
														<span class="caption text--secondary ml-1">({{ formatPercentage(statistics.forecast.margin.at_completion_percentage) }})</span>
													</template>
													<span v-else class="subtitle-1 font-weight-bold text--secondary">n.s.</span>
												</div>
											</template>
										</help-hint>
									</div>
								</v-col>
							</v-row>
						</v-card-text>
						<v-data-table
							:headers="forecastHeaders"
							:items="forecastRows"
							item-key="key"
							disable-sort
							disable-pagination
							hide-default-footer
							class="elevation-0 background-2"
						>
							<!-- "Ordinato" e' corto in tabella: il significato completo nell'aiuto -->
							<template v-slot:[`header.committed`]="{ header }">
								<span class="text-no-wrap">
									{{ header.text }}
									<help-hint text="Ordinato ai fornitori, non ancora arrivato: la parte non evasa degli ordini di acquisto."></help-hint>
								</span>
							</template>
							<template v-slot:[`item.label`]="{ item }">
								<span class="text-no-wrap">
									<v-icon small :color="item.color" class="mr-1">{{ item.icon }}</v-icon>
									<span class="font-weight-medium">{{ item.label }}</span>
								</span>
							</template>
							<template v-slot:[`item.planned`]="{ item }">
								<help-hint :text="item.plannedHint">
									<template v-slot:activator="{ on, attrs }">
										<span class="text-no-wrap" :class="{ 'text--disabled': item.plannedWarning === 'unreliable' }" style="cursor: pointer" v-bind="attrs" v-on="on">
											<v-icon v-if="item.plannedWarning" x-small :color="item.plannedWarning === 'unreliable' ? 'error' : 'warning'" class="mr-1">mdi-alert-outline</v-icon>
											{{ item.planned | currency }}
										</span>
									</template>
								</help-hint>
							</template>
							<template v-slot:[`item.committed`]="{ item }">
								<help-hint :text="item.committedHint" :disabled="item.committed === null">
									<template v-slot:activator="{ on, attrs }">
										<span class="text-no-wrap" style="cursor: pointer" v-bind="attrs" v-on="on">{{ item.committed === null ? '—' : $options.filters.currency(item.committed) }}</span>
									</template>
								</help-hint>
							</template>
							<template v-slot:[`item.actual`]="{ item }">
								<help-hint :text="item.actualHint" max-width="340">
									<template v-slot:activator="{ on, attrs }">
										<span class="text-no-wrap font-weight-bold" style="cursor: pointer" v-bind="attrs" v-on="on">{{ item.actual | currency }}</span>
									</template>
								</help-hint>
							</template>
							<template v-slot:[`item.variance`]="{ item }">
								<help-hint :text="item.varianceHint">
									<template v-slot:activator="{ on, attrs }">
										<span class="text-no-wrap" :class="item.varianceClass" style="cursor: pointer" v-bind="attrs" v-on="on">
											{{ signedCurrency(item.variance) }}
											<span class="caption ml-1">({{ formatPercentage(item.variancePercentage, true) }})</span>
										</span>
									</template>
								</help-hint>
							</template>
							<template v-slot:[`item.progress`]="{ item }">
								<help-hint :text="item.progressHint">
									<template v-slot:activator="{ on, attrs }">
										<div v-if="item.progress !== null" class="d-flex align-center" style="min-width: 120px; cursor: pointer" v-bind="attrs" v-on="on">
											<v-progress-linear :value="Math.min(item.progress, 100)" :color="item.progressColor" height="8" rounded class="mr-2"></v-progress-linear>
											<span class="caption text-no-wrap">{{ formatPercentage(item.progress) }}</span>
										</div>
										<span v-else style="cursor: pointer" v-bind="attrs" v-on="on">—</span>
									</template>
								</help-hint>
							</template>
						</v-data-table>
						<!-- Ricavi e costi ad oggi nel dettaglio (consegnato, fatturato, da fatturare, ricevuto...): la ✓ indica
							il documento che conta in "Ad oggi", il più alto tra consegnato e fatturato -->
						<v-card-text v-if="forecastDetails" class="pt-2 pb-1">
							<v-row dense>
								<v-col v-for="group in forecastDetails" :key="group.key" cols="12" :md="group.key === 'revenue' ? 7 : 5">
									<div class="caption font-weight-medium mb-1">
										<v-icon x-small :color="group.color" class="mr-1">{{ group.icon }}</v-icon>{{ group.label }}
									</div>
									<div class="d-flex flex-wrap">
										<help-hint v-for="entry in group.entries" :key="entry.key" :text="entry.hint">
											<template v-slot:activator="{ on, attrs }">
												<div class="mr-5 mb-1" style="cursor: pointer" v-bind="attrs" v-on="on">
													<div class="caption text--secondary text-no-wrap">
														{{ entry.label }}
														<v-icon v-if="entry.counted" x-small color="success">mdi-check</v-icon>
													</div>
													<div class="body-2 font-weight-bold text-no-wrap">{{ entry.value | currency }}</div>
												</div>
											</template>
										</help-hint>
									</div>
								</v-col>
							</v-row>
						</v-card-text>
						<!-- Costo previsto parziale o non affidabile (forecast.cost.planned_reliability): invito a impostare il budget -->
						<v-card-text v-if="plannedCostNote" class="pt-1 pb-0 caption" :class="plannedCostNote.unreliable ? 'error--text' : 'text--secondary'">
							<v-icon x-small class="mr-1" :color="plannedCostNote.unreliable ? 'error' : ''">mdi-alert-outline</v-icon>
							{{ plannedCostNote.text }}
							<router-link v-if="canEditProject" :to="{ name: 'projects-edit', params: { id: id } }" class="ml-1">Imposta il budget costi del progetto</router-link>
						</v-card-text>
						<!-- Preventivi in trattativa (forecast.revenue.pending_quotations): solo informativi, non nel previsto -->
						<v-card-text v-if="pendingQuotations" class="pt-1 pb-2 caption text--secondary">
							<help-hint text="Preventivi non ancora accettati né rifiutati: non entrano nel previsto.">
								<template v-slot:activator="{ on, attrs }">
									<span style="cursor: pointer" v-bind="attrs" v-on="on">
										<v-icon x-small class="mr-1">mdi-information-outline</v-icon>
										In trattativa: {{ pendingQuotations.count }} {{ pendingQuotations.count === 1 ? 'preventivo' : 'preventivi' }}, {{ pendingQuotations.subtotal | currency }}
									</span>
								</template>
							</help-hint>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<!-- Preventivi Collegati -->
			<v-row v-if="quotationsList.length > 0">
				<v-col cols="12" class="pa-2 pt-0">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="primary">mdi-file-document-edit-outline</v-icon>
							Preventivi Collegati
							<v-chip small class="ml-2">{{ quotationsList.length }}</v-chip>
						</v-card-title>
						<v-divider></v-divider>
						<v-data-table
							:headers="quotationHeaders"
							:items="quotationsList"
							:items-per-page="10"
							class="elevation-0"
						>
							<template v-slot:[`item.number`]="{ item }">
								<router-link :to="documentUrl('quotations', item.id)" class="text-decoration-underline">
									{{ item.number + (item.sectional ? '/' + item.sectional : '') }}
								</router-link>
								<v-icon v-if="item.partial" color="warning" class="ml-1" title="Documento su più progetti">mdi-call-split</v-icon>
							</template>
							<template v-slot:[`item.date`]="{ item }">
								{{ item.date | date }}
							</template>
							<template v-slot:[`item.subtotal`]="{ item }">
								<span class="font-weight-bold">{{ item.subtotal | currency }}</span>
							</template>
						</v-data-table>
						<v-divider></v-divider>
						<v-card-actions class="background-2 pa-3">
							<v-row class="mx-n1">
								<v-col cols="12" sm="3" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="info">mdi-file-document-multiple</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">N° Preventivi</div>
											<div class="font-weight-bold">{{ quotationsList.length }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="3" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="primary">mdi-currency-eur</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Preventivato</div>
											<div class="font-weight-bold">{{ totalQuotationsAmount | currency }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="3" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="success">mdi-cart</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Venduto (ordini)</div>
											<div class="font-weight-bold">{{ soldAmount | currency }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="3" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon :color="quotationConversionPercentage >= 100 ? 'success' : 'warning'">mdi-percent</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Conversione</div>
											<div class="font-weight-bold">{{ quotationConversionPercentage }}%</div>
										</div>
									</div>
								</v-col>
							</v-row>
						</v-card-actions>
					</v-card>
				</v-col>
			</v-row>

			<!-- Confronto Documenti -->
			<v-row>
				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							Riepilogo Documenti Attivi (Ricavi)
						</v-card-title>
						<v-divider></v-divider>
						<v-data-table
							v-if="activeDocumentsList"
							:headers="documentSummaryHeaders"
							:items="activeSummaryRows"
							:item-class="summaryRowClass"
							hide-default-footer
							class="elevation-0"
						>
							<!-- Colonna "Conteggiato" (kpiRows): ✓ se la riga entra nel KPI, altrimenti il motivo -->
							<template v-slot:[`item.subtotal`]="{ item }">
								<span :class="{ 'font-weight-bold': !item.kpiExcluded }">{{ item.amount }}</span>
							</template>
							<template v-slot:[`item.kpiStatus`]="{ item }">
								<span v-if="item.kpiExcluded && item.kpiHint" class="caption">
									{{ item.kpiReason }}
									<help-hint :text="item.kpiHint"></help-hint>
								</span>
								<span v-else-if="item.kpiExcluded" class="caption">{{ item.kpiReason }}</span>
								<span v-else>
									<v-icon small color="success">mdi-check</v-icon>
									<span v-if="item.kpiNote" class="caption text--secondary ml-1">{{ item.kpiNote }}</span>
								</span>
							</template>
						</v-data-table>
						<v-divider></v-divider>
						<v-card-actions class="background-2 pa-3">
							<v-spacer></v-spacer>
							<div class="d-flex align-center mr-4">
								<v-icon color="info">mdi-file-document-multiple</v-icon>
								<div class="ml-2">
									<div class="caption text--secondary">N° Documenti</div>
									<div class="font-weight-bold">{{ totalActiveDocumentsCount }}</div>
								</div>
							</div>
							<!-- Stesso valore del KPI Ricavi ad oggi -->
							<div class="d-flex align-center mr-4">
								<v-icon color="success">mdi-currency-eur</v-icon>
								<div class="ml-2">
									<div class="caption text--secondary d-flex align-center">
										Totale ricavi
										<help-hint class="ml-1" text="Somma delle righe con ✓: è il valore del KPI Ricavi ad oggi."></help-hint>
									</div>
									<div class="font-weight-bold">{{ calculatedRevenue | currency }}</div>
								</div>
							</div>
							<v-spacer></v-spacer>
						</v-card-actions>
					</v-card>
				</v-col>

				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card  background-2">
						<v-card-title class="py-2">
							Riepilogo Documenti Passivi (Costi)
							<v-spacer></v-spacer>
							<v-menu v-if="newDocumentItems.length > 0" offset-y left>
								<template v-slot:activator="{ on, attrs }">
									<v-btn small outlined color="primary" v-bind="attrs" v-on="on">
										<v-icon left small>mdi-plus</v-icon>
										Nuovo costo
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item v-for="item in newDocumentItems" :key="item.role" @click="newCommissionDocument(item)">
										<v-list-item-icon class="mr-2"><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title>{{ item.label }}</v-list-item-title>
											<v-list-item-subtitle>{{ item.hint }}</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
							<!-- Senza causali nelle impostazioni progetti il menu non ha voci: chi puo' configurarle ci arriva da qui.
								Per percorso e non per nome: 'settingsprojects' e' la rotta padre e per nome Vue Router non mostra
								la figlia (ProjectsSettings), la pagina restava sullo spinner -->
							<v-tooltip v-else-if="canConfigureCommissionCausals" bottom>
								<template v-slot:activator="{ on }">
									<v-btn small text color="primary" :to="{ path: '/settingsprojects/', query: { tab: 'altro' } }" v-on="on">
										<v-icon left small>mdi-cog-outline</v-icon>
										Causali commessa
									</v-btn>
								</template>
								<span>Per creare consumi, rientri e costi commessa scegli le causali in Impostazioni Progetti, scheda Altro</span>
							</v-tooltip>
						</v-card-title>
						<v-divider></v-divider>
						<v-data-table
						v-if="passiveDocumentsList"
							:headers="documentSummaryHeaders"
							:items="passiveSummaryRows"
							:item-class="summaryRowClass"
							hide-default-footer
							class="elevation-0"
							>
							<!-- Colonna "Conteggiato" (kpiRows): ✓ se la riga entra nel KPI, altrimenti il motivo -->
							<template v-slot:[`item.subtotal`]="{ item }">
								<span :class="{ 'font-weight-bold': !item.kpiExcluded }">{{ item.amount }}</span>
							</template>
							<template v-slot:[`item.kpiStatus`]="{ item }">
								<span v-if="item.kpiExcluded && item.kpiHint" class="caption">
									{{ item.kpiReason }}
									<help-hint :text="item.kpiHint"></help-hint>
								</span>
								<span v-else-if="item.kpiExcluded" class="caption">{{ item.kpiReason }}</span>
								<span v-else>
									<v-icon small color="success">mdi-check</v-icon>
									<span v-if="item.kpiNote" class="caption text--secondary ml-1">{{ item.kpiNote }}</span>
								</span>
							</template>
						</v-data-table>
						<v-divider></v-divider>
						<v-card-actions class="background-2 pa-3">
							<v-spacer></v-spacer>
							<div class="d-flex align-center mr-4">
								<v-icon color="info">mdi-file-document-multiple</v-icon>
								<div class="ml-2">
									<div class="caption text--secondary">N° Documenti</div>
									<div class="font-weight-bold">{{ totalPassiveDocumentsCount }}</div>
								</div>
							</div>
							<!-- Stesso valore del KPI Costi ad oggi -->
							<div class="d-flex align-center mr-4">
								<v-icon color="error">mdi-currency-eur</v-icon>
								<div class="ml-2">
									<div class="caption text--secondary d-flex align-center">
										Totale costi
										<help-hint class="ml-1" text="Somma delle righe con ✓: è il valore del KPI Costi ad oggi."></help-hint>
									</div>
									<div class="font-weight-bold">{{ calculatedCost | currency }}</div>
								</div>
							</div>
							<v-spacer></v-spacer>
						</v-card-actions>
					</v-card>
				</v-col>
			</v-row>

			<!-- Quadratura materiali: entrate come costo contro uscite verso il cliente, per prodotto -->
			<v-row v-if="reconciliation.summary">
				<v-col cols="12" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="primary">mdi-scale-balance</v-icon>
							Quadratura materiali
							<help-hint small class="ml-1" max-width="340" text="Per ogni prodotto confronta il materiale entrato come costo (acquisti, consumi) con quello uscito verso il cliente (DDT, fatture, ticket). Preso da magazzino: uscito senza acquisto, il costo è già in Materiale da magazzino; Comprato, da consegnare: acquistato ma non ancora uscito."></help-hint>
							<v-spacer></v-spacer>
							<v-chip small outlined class="mr-1" :color="reconciliation.summary.uncovered > 0 ? reconciliationStatusColor('uncovered') : 'grey'">
								Preso da magazzino: {{ reconciliation.summary.uncovered }}
								<span v-if="reconciliation.summary.uncovered_value" class="ml-1">({{ reconciliation.summary.uncovered_value | currency }})</span>
							</v-chip>
							<v-chip small outlined class="mr-1" :color="reconciliation.summary.surplus > 0 ? 'warning' : 'grey'">
								Comprato, da consegnare: {{ reconciliation.summary.surplus }}
								<span v-if="reconciliation.summary.surplus_value" class="ml-1">({{ reconciliation.summary.surplus_value | currency }})</span>
							</v-chip>
							<v-chip v-if="reconciliation.summary.planned" small outlined class="mr-1" color="info">Solo previsto: {{ reconciliation.summary.planned }}</v-chip>
							<v-chip small outlined color="success">Quadrano: {{ reconciliation.summary.balanced }}</v-chip>
						</v-card-title>
						<v-divider></v-divider>
						<v-card-text class="py-2">
							<v-row dense align="center">
								<v-col cols="12" md="4">
									<v-text-field
										v-model="reconciliation.search"
										label="Cerca prodotto o codice"
										append-icon="mdi-magnify"
										dense
										outlined
										hide-details
										clearable
									></v-text-field>
								</v-col>
								<v-col cols="12" md="3">
									<v-switch v-model="reconciliation.onlyIssues" label="Solo da sistemare" dense hide-details class="mt-0"></v-switch>
								</v-col>
								<v-col cols="12" md="3">
									<v-switch v-model="reconciliation.allProducts" label="Includi prodotti non a magazzino" dense hide-details class="mt-0" @change="loadReconciliation"></v-switch>
								</v-col>
								<v-col cols="12" md="2" class="text-right caption text--secondary">
									Entrate {{ reconciliation.summary.in_value | currency }}<br>
									Uscite {{ reconciliation.summary.out_value | currency }}
								</v-col>
							</v-row>
						</v-card-text>
						<v-divider></v-divider>
						<v-data-table
							:headers="reconciliationHeaders"
							:items="reconciliationRows"
							:loading="reconciliation.loading"
							:items-per-page="10"
							item-key="product_id"
							class="elevation-0 background-2"
						>
							<template v-slot:[`item.code`]="{ item }">
								<router-link :to="'/products/' + item.product_id" class="text-decoration-underline">{{ item.code }}</router-link>
							</template>
							<template v-slot:[`item.purchased_quantity`]="{ item }">
								{{ item.purchased_quantity }}
								<span v-if="item.purchase_source === 'invoice'" class="caption grey--text">(fatt.)</span>
								<span v-else-if="item.purchase_source === 'order'" class="caption grey--text">(ord.)</span>
							</template>
							<template v-slot:[`item.ddt_quantity`]="{ item }">
								{{ item.ddt_quantity }}
								<span v-if="item.invoice_quantity" class="caption grey--text">+ {{ item.invoice_quantity }} fatt.</span>
							</template>
							<template v-slot:[`item.difference`]="{ item }">
								<span :class="reconciliationStatusColor(item.status) + '--text font-weight-bold'">{{ item.difference }}</span>
							</template>
							<!-- Preso da magazzino: il valore è un costo già conteggiato (Materiale da magazzino), senza segno -->
							<template v-slot:[`item.difference_value`]="{ item }">
								<span :class="reconciliationStatusColor(item.status) + '--text'">{{ (item.status === 'uncovered' ? Math.abs(item.difference_value) : item.difference_value) | currency }}</span>
							</template>
							<template v-slot:[`item.status`]="{ item }">
								<v-chip x-small :color="reconciliationStatusColor(item.status)" dark>{{ reconciliationStatusText(item.status) }}</v-chip>
							</template>
							<template v-slot:no-data>
								<span v-if="reconciliation.onlyIssues">Tutti i materiali quadrano</span>
								<span v-else>Nessun materiale nei documenti della commessa</span>
							</template>
						</v-data-table>
						<div v-if="reconciliation.summary.excluded_troubles > 0 || reconciliation.summary.truncated" class="caption grey--text px-4 pb-2">
							<span v-if="reconciliation.summary.excluded_troubles > 0">{{ reconciliation.summary.excluded_troubles }} ticket convertiti in DDT: i loro materiali contano nel DDT. </span>
							<span v-if="reconciliation.summary.truncated">Elenco limitato ai primi 1000 prodotti.</span>
						</div>
					</v-card>
				</v-col>
			</v-row>

			<!-- Lista Documenti Dettagliata - Due Pannelli Affiancati -->
			<v-row>
				<!-- Pannello Documenti Attivi -->
				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2 pb-4">
						<v-card-title class="py-2">
							<v-icon left color="success">mdi-file-document-multiple</v-icon>
							Documenti Attivi (Ricavi)
						</v-card-title>
						<v-divider></v-divider>
						<v-card-text class="pa-0 text-center">
							<v-tabs v-model="activeDocumentTab" show-arrows center-active fixed-tabs>
								<v-tab v-for="(doc, type) in filteredActiveDocumentTypes" :key="type">
									<v-icon left small>{{ doc.icon }}</v-icon>
									{{ doc.label }}
									<v-chip x-small class="ml-2">
										{{ getDocumentCount(type) }}
									</v-chip>
								</v-tab>
							</v-tabs>
							<v-tabs-items v-model="activeDocumentTab">
								<v-tab-item v-for="(doc, type) in filteredActiveDocumentTypes" :key="type" class="background-2">
									<v-data-table
										:headers="getActiveDocumentHeaders()"
										:items="documents[type] || []"
										:items-per-page="5"
										:expanded.sync="expandedActive[type]"
										item-key="id"
										show-expand
										class="elevation-0"
										@item-expanded="onItemExpanded($event, type)"
									>
										<template v-slot:[`item.number`]="{ item }">
											<router-link :to="documentUrl(type, item.id)" class="text-decoration-underline">
												{{ item.number + (item.sectional ? '/' + item.sectional : '') }}
											</router-link>
											<v-chip v-if="isSpecialCausal(item)" x-small outlined class="ml-1" :color="causalChipColor(item)">{{ item.causal }}</v-chip>
											<v-icon v-if="item.partial" color="warning" class="ml-1" title="Documento su più progetti">mdi-call-split</v-icon>
										</template>
										<template v-slot:[`item.date`]="{ item }">
											{{ item.date | date }}
										</template>
										<template v-slot:[`item.subtotal`]="{ item }">
											<span v-if="isExcludedDocument(item)" class="text--disabled" :title="excludedDocumentHint">
												{{ item.subtotal | currency }} <span class="caption">fuori commessa</span>
											</span>
											<span v-else class="font-weight-bold">{{ item.subtotal | currency }}</span>
										</template>
										<template v-slot:[`item.company`]="{ item }">
											{{ getCompanyName(item) }}
										</template>
										<template v-slot:expanded-item="{ headers, item }">
											<td :colspan="headers.length" class="pa-0">
												<v-progress-linear v-if="loadingProducts[type] && loadingProducts[type][item.id]" indeterminate></v-progress-linear>
												<v-simple-table v-else dense class="background-4">
													<template v-slot:default>
														<thead>
															<tr>
																<th class="text-left">Codice</th>
																<th class="text-left">Prodotto</th>
																<th class="text-right">Quantità</th>
																<th class="text-right">Prezzo</th>
																<th class="text-right">Totale</th>
															</tr>
														</thead>
														<tbody>
															<tr v-for="product in (documentProducts[type] && documentProducts[type][item.id]) || []" :key="product.id">
																<td class="text-left">
																	<router-link :to="'/products/' + product.id" class="text-decoration-underline">
																		{{ product.code }}
																	</router-link>
																</td>
																<td class="text-left">{{ product.name }}</td>
																<td class="text-right">{{ product.quantity }}</td>
																<td class="text-right">{{ product.price | currency }}</td>
																<td class="text-right font-weight-bold">{{ product.total | currency }}</td>
															</tr>
														</tbody>
													</template>
												</v-simple-table>
											</td>
										</template>
									</v-data-table>
								</v-tab-item>
							</v-tabs-items>
						</v-card-text>
					</v-card>
				</v-col>

				<!-- Pannello Documenti Passivi -->
				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2 pb-4">
						<v-card-title class="py-2">
							<v-icon left color="error">mdi-file-document-multiple</v-icon>
							Documenti Passivi (Costi)
							<v-btn icon small class="ml-1" title="Come vengono contati i costi" @click="rulesDialog = true">
								<v-icon small>mdi-help-circle-outline</v-icon>
							</v-btn>
						</v-card-title>
						<v-divider></v-divider>
						<v-card-text class="pa-0 text-center">
							<v-tabs v-model="passiveDocumentTab" show-arrows center-active fixed-tabs>
								<v-tab v-for="(doc, type) in filteredPassiveDocumentTypes" :key="type">
									<v-icon left small>{{ doc.icon }}</v-icon>
									{{ doc.label }}
									<v-chip x-small class="ml-2">
										{{ getDocumentCount(type) }}
									</v-chip>
								</v-tab>
							</v-tabs>
							<v-tabs-items v-model="passiveDocumentTab">
								<v-tab-item v-for="(doc, type) in filteredPassiveDocumentTypes" :key="type" class="background-2">
									<v-data-table
										:headers="getPassiveDocumentHeaders(type)"
										:items="documents[type] || []"
										:items-per-page="5"
										:expanded.sync="expandedPassive[type]"
										item-key="id"
										show-expand
										class="elevation-0"
										@item-expanded="onItemExpanded($event, type)"
									>
										<template v-slot:[`item.number`]="{ item }">
											<router-link :to="documentUrl(type, item.id)" class="text-decoration-underline">
												{{ item.number + (item.sectional ? '/' + item.sectional : '') }}
											</router-link>
											<v-chip v-if="isSpecialCausal(item)" x-small outlined class="ml-1" :color="causalChipColor(item)">{{ item.causal }}</v-chip>
											<v-icon v-if="item.partial" color="warning" class="ml-1" title="Documento su più progetti">mdi-call-split</v-icon>
										</template>
										<template v-slot:[`item.date`]="{ item }">
											{{ item.date | date }}
										</template>
										<template v-slot:[`item.amount`]="{ item }">
											<span v-if="isExcludedDocument(item)" class="text--disabled" :title="excludedDocumentHint">
												{{ item.subtotal | currency }} <span class="caption">fuori commessa</span>
											</span>
											<span v-else class="font-weight-bold">{{ item.subtotal | currency }}</span>
										</template>
										<template v-slot:[`item.company`]="{ item }">
											{{ getCompanyName(item) }}
										</template>
										<template v-slot:expanded-item="{ headers, item }">
											<td :colspan="headers.length" class="pa-0">
												<v-progress-linear v-if="loadingProducts[type] && loadingProducts[type][item.id]" indeterminate></v-progress-linear>
												<v-simple-table v-else dense class="background-4">
													<template v-slot:default>
														<thead>
															<tr>
																<th class="text-left">Codice</th>
																<th class="text-left">Prodotto</th>
																<th class="text-right">Quantità</th>
																<th class="text-right">Prezzo</th>
																<th class="text-right">Totale</th>
															</tr>
														</thead>
														<tbody>
															<tr v-for="product in (documentProducts[type] && documentProducts[type][item.id]) || []" :key="product.id">
																<td class="text-left">
																	<router-link :to="'/products/' + product.id" class="text-decoration-underline">
																		{{ product.code }}
																	</router-link>
																</td>
																<td class="text-left">{{ product.name }}</td>
																<td class="text-right">{{ product.quantity }}</td>
																<td class="text-right">{{ product.price | currency }}</td>
																<td class="text-right font-weight-bold">{{ product.total | currency }}</td>
															</tr>
														</tbody>
													</template>
												</v-simple-table>
											</td>
										</template>
									</v-data-table>
								</v-tab-item>
							</v-tabs-items>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<!-- Analisi Prodotti per Documento - Due Pannelli Affiancati -->
			<v-row>
				<!-- Pannello Prodotti Documenti Attivi -->
				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="success">mdi-package-variant-closed</v-icon>
							Prodotti Documenti Attivi
						</v-card-title>
						<v-divider></v-divider>
						<v-card-text class="pa-3">
							<v-row class="ma-0">
								<v-col cols="12" sm="6" class="py-1 px-1">
									<v-select
										v-model="selectedActiveDocType"
										:items="activeDocumentTypesList"
										label="Tipo Documento"
										dense
										outlined
										hide-details
										:menu-props="{'offset-y': true}"
										@change="onActiveDocTypeChange"
									></v-select>
								</v-col>
								<v-col cols="12" sm="6" class="py-1 px-1">
									<v-text-field
										v-model="searchActiveProduct"
										label="Cerca prodotto o codice"
										dense
										outlined
										clearable
										hide-details
										append-icon="mdi-magnify"
										@input="onSearchActiveProduct"
									></v-text-field>
								</v-col>
							</v-row>
						</v-card-text>
						<v-divider></v-divider>
						<v-data-table
							:headers="typeProductHeaders"
							:items="typeDocumentProducts.active"
							:options.sync="activeProductDocumentPagination"
							:server-items-length="typeDocumentProductsTotal.active"
							:loading="loadingTypeProducts.active"
							:item-class="excludedRowClass"
							class="elevation-0"
						>
							<template v-slot:[`item.document_number`]="{ item }">
								<router-link :to="documentUrl(selectedActiveDocType, item.document_id)" class="text-decoration-underline">
									{{ item.document_number }}
								</router-link>
							</template>
							<template v-slot:[`item.code`]="{ item }">
								<router-link :to="'/products/' + item.product_id" class="text-decoration-underline">
									{{ item.code }}
								</router-link>
							</template>
							<template v-slot:[`item.price`]="{ item }">
								{{ item.price | currency }}
							</template>
							<template v-slot:[`item.total`]="{ item }">
								<span class="font-weight-bold">{{ (item.total ? item.total : 0) | currency }}</span>
							</template>
							<template v-slot:[`footer.page-text`]="{ pageStart, pageStop }">
								Da {{ pageStart }} a {{ pageStop }} di {{ typeDocumentProductsTotal.active }}
							</template>
						</v-data-table>
						<v-divider></v-divider>
						<v-card-actions class="background-2 pa-3">
							<v-row class="mx-n1">
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="info">mdi-file-document-multiple</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">N° Documenti</div>
											<div class="font-weight-bold">{{ activeDocsCount }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="primary">mdi-counter</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Quantità Totale</div>
											<div class="font-weight-bold">{{ activeProductsQuantity }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="success">mdi-currency-eur</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Valore Totale</div>
											<div class="font-weight-bold">{{ activeProductsValue | currency }}</div>
										</div>
									</div>
								</v-col>
							</v-row>
						</v-card-actions>
					</v-card>
				</v-col>

				<!-- Pannello Prodotti Documenti Passivi -->
				<v-col cols="12" md="6" class="pa-2">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="error">mdi-package-variant-closed</v-icon>
							Prodotti Documenti Passivi
						</v-card-title>
						<v-divider></v-divider>
						<v-card-text class="pa-3">
							<v-row class="ma-0">
								<v-col cols="12" sm="6" class="py-1 px-1">
									<v-select
										v-model="selectedPassiveDocType"
										:items="passiveDocumentTypesList"
										label="Tipo Documento"
										dense
										outlined
										hide-details
										:menu-props="{'offset-y': true}"
										@change="onPassiveDocTypeChange"
									></v-select>
								</v-col>
								<v-col cols="12" sm="6" class="py-1 px-1">
									<v-text-field
										v-model="searchPassiveProduct"
										label="Cerca prodotto o codice"
										dense
										outlined
										clearable
										hide-details
										append-icon="mdi-magnify"
										@input="onSearchPassiveProduct"
									></v-text-field>
								</v-col>
							</v-row>
						</v-card-text>
						<v-divider></v-divider>
						<v-data-table
							:headers="typeProductHeaders"
							:items="typeDocumentProducts.passive"
							:options.sync="passiveProductDocumentPagination"
							:server-items-length="typeDocumentProductsTotal.passive"
							:loading="loadingTypeProducts.passive"
							:item-class="excludedRowClass"
							class="elevation-0"
						>
							<template v-slot:[`item.document_number`]="{ item }">
								<router-link :to="documentUrl(selectedPassiveDocType, item.document_id)" class="text-decoration-underline">
									{{ item.document_number }}
								</router-link>
							</template>
							<template v-slot:[`item.code`]="{ item }">
								<router-link :to="'/products/' + item.product_id" class="text-decoration-underline">
									{{ item.code }}
								</router-link>
							</template>
							<template v-slot:[`item.price`]="{ item }">
								{{ item.price | currency }}
							</template>
							<template v-slot:[`item.total`]="{ item }">
								<span v-if="item.commission_excluded" :title="excludedDocumentHint">
									{{ item.total | currency }} <span class="caption">fuori commessa</span>
								</span>
								<span v-else class="font-weight-bold">{{ item.total | currency }}</span>
							</template>
							<template v-slot:[`footer.page-text`]="{ pageStart, pageStop }">
								Da {{ pageStart }} a {{ pageStop }} di {{ typeDocumentProductsTotal.passive }}
							</template>
						</v-data-table>
						<v-divider></v-divider>
						<v-card-actions class="background-2 pa-3">
							<v-row class="mx-n1">
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="info">mdi-file-document-multiple</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">N° Documenti</div>
											<div class="font-weight-bold">{{ passiveDocsCount }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="primary">mdi-counter</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Quantità Totale</div>
											<div class="font-weight-bold">{{ passiveProductsQuantity }}</div>
										</div>
									</div>
								</v-col>
								<v-col cols="12" sm="4" class="py-1 px-1">
									<div class="d-flex align-center">
										<v-icon color="error">mdi-currency-eur</v-icon>
										<div class="ml-2">
											<div class="caption text--secondary">Valore Totale</div>
											<div class="font-weight-bold">{{ passiveProductsValue | currency }}</div>
										</div>
									</div>
								</v-col>
							</v-row>
						</v-card-actions>
					</v-card>
				</v-col>
			</v-row>

			<!-- Tabella Ticket -->
			<v-row v-if="documents.troubles && documents.troubles.length > 0">
				<v-col cols="12" class="pa-3">
					<v-card flat class="elevation-1 round-card background-2">
						<v-card-title class="py-2">
							<v-icon left color="warning">mdi-ticket</v-icon>
							Ticket Collegati
							<v-chip small class="ml-2">{{ documents.troubles.length }}</v-chip>
						</v-card-title>
						<v-divider></v-divider>
						<v-data-table
							:headers="ticketHeaders"
							:items="documents.troubles"
							:items-per-page="10"
							:expanded.sync="expandedTickets"
							item-key="id"
							show-expand
							class="elevation-0 background-2"
							@item-expanded="onTicketExpanded"
						>
							<template v-slot:[`item.number`]="{ item }">
								<router-link :to="documentUrl('troubles', item.id)" class="text-decoration-underline">
									#{{ item.number }}
								</router-link>
							</template>
							<template v-slot:[`item.created_at`]="{ item }">
								{{ item.created_at | date }}
							</template>
							<template v-slot:[`item.subtotal`]="{ item }">
								{{ item.subtotal | currency }}
								<v-tooltip v-if="item.converted_ddt_id" bottom>
									<template v-slot:activator="{ on }">
										<v-chip v-on="on" x-small color="info" dark class="ml-1" :to="documentUrl('ddts', item.converted_ddt_id)">
											In DDT {{ item.converted_ddt_number }}
										</v-chip>
									</template>
									<span>Ticket convertito in DDT: ricavi e materiali contano nel DDT, qui resta solo il costo della manodopera</span>
								</v-tooltip>
							</template>
							<template v-slot:[`item.materials_revenue`]="{ item }">
								<span :class="item.converted_ddt_id ? 'grey--text' : 'success--text'">{{ item.materials_revenue | currency }}</span>
							</template>
							<template v-slot:[`item.materials_cost`]="{ item }">
								<span class="grey--text">{{ item.materials_cost | currency }}</span>
							</template>
							<template v-slot:[`item.interventions_cost`]="{ item }">
								<span class="error--text">{{ (item.interventions_cost || 0) | currency }}</span>
							</template>
							<template v-slot:expanded-item="{ headers, item }">
								<td :colspan="headers.length" class="pa-0">
									<v-progress-linear v-if="loadingInterventions[item.id]" indeterminate></v-progress-linear>
									<v-simple-table v-else dense class="background-4">
										<template v-slot:default>
											<thead>
												<tr>
													<th class="text-left">Operatore</th>
													<th class="text-left">Tipo</th>
													<th class="text-center">Data Inizio</th>
													<th class="text-center">Data Fine</th>
													<th class="text-center">Durata</th>
													<th class="text-right">Totale</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="intervention in ticketInterventions[item.id] || []" :key="intervention.id">
													<td>{{ intervention.operator }}</td>
													<td>{{ intervention.type_text }}</td>
													<td class="text-center">{{ intervention.start_date | datetime }}</td>
													<td class="text-center">{{ intervention.end_date | datetime }}</td>
													<td class="text-center">{{ formatDuration(intervention.duration_minutes) }}</td>
													<td class="text-right">{{ intervention.total | currency }}</td>
												</tr>
												<tr v-if="!ticketInterventions[item.id] || ticketInterventions[item.id].length === 0">
													<td colspan="6" class="text-center pa-3">Nessun intervento</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>
								</td>
							</template>
						</v-data-table>
					</v-card>
				</v-col>
			</v-row>
		</v-col>

        <v-tooltip top>
            <template v-slot:activator="{ on: tooltip }">
                <v-fab-transition>
                    <v-btn
                        v-on="{ ...tooltip }"
                        @click.native="$eventBusUtility.goTo($router, 'projects-edit', id, null, {})"
                        color="primary"
                        dark
                        fixed
                        bottom
                        right
                        fab>
                        <v-icon>edit</v-icon>
                    </v-btn>
                </v-fab-transition>
            </template>
            <span>Modifica</span>
        </v-tooltip>

		<!-- Regole di conteggio dei costi -->
		<v-dialog v-model="rulesDialog" max-width="640" scrollable>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<v-icon left color="primary">mdi-help-circle-outline</v-icon>
					<p class="pl-2 pl-xl-3 mb-0">Come vengono contati ricavi e costi della commessa</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<div v-for="(rule, index) in commissionRules" :key="index" class="mb-4">
						<div class="font-weight-bold mb-1">{{ rule.title }}</div>
						<div class="body-2">{{ rule.text }}</div>
					</div>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="pa-3">
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="rulesDialog = false">Chiudi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>

import Show from '@/components/common/Show'
import HelpHint from '@/components/common/HelpHint'
import CommissionKpiCustomize from '@/components/modules/projects/CommissionKpiCustomize'

export default {
	name: 'ProjectCommission',
	components: {
		Show,
		HelpHint,
		CommissionKpiCustomize
	},
	props: {
		id: {
			type: Number,
			required: true
		},
		models: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			projectModels: {},
			infos: {},
			loading: false,
			initialized: false,
			activeDocumentTab: 0,
			passiveDocumentTab: 0,
			statistics: {
				active_documents: {},
				passive_documents: {},
				material_costs: {},
				totals: {
					revenue: 0,
					cost: 0,
					margin: 0,
					margin_percentage: 0
				},
				troubles: null,
				forecast: null,
				warnings: {},
				new_documents: {}
			},
			// Card "Previsto e consuntivo": etichette senza gergo contabile (Ordinato = impegnato, Ad oggi = consuntivo)
			forecastHeaders: [
				{ text: '', value: 'label', align: 'left' },
				{ text: 'Previsto', value: 'planned', align: 'right' },
				{ text: 'Ordinato', value: 'committed', align: 'right' },
				{ text: 'Ad oggi', value: 'actual', align: 'right' },
				{ text: 'Differenza dal previsto', value: 'variance', align: 'right' },
				{ text: 'Avanz.', value: 'progress', align: 'left', width: 170 }
			],
			// Flussi dei documenti per i KPI: l'ordine e' il previsto (vendita) o l'ordinato (acquisto) e non entra mai
			// in "ad oggi"; tra consegnato (DDT) e fatturato (fatture con le note in sottrazione) conta il piu' alto,
			// cosi' una fattura d'acconto non fa scendere i ricavi sotto il consegnato (come Project::commissionForecast)
			revenueFlow: {
				order: 'saleorders',
				orderReason: 'È il previsto',
				orderHint: 'Gli ordini di vendita sono il contratto: entrano nel Previsto, non nei ricavi ad oggi.',
				delivered: 'ddts',
				deliveredText: 'consegnato',
				invoiced: ['saleinvoices', 'creditnotes'],
				invoicedText: 'fatturato'
			},
			costFlow: {
				order: 'purchaseorders',
				orderReason: 'È l\'ordinato',
				orderHint: 'Gli ordini di acquisto non ancora arrivati sono nella colonna Ordinato della card Previsto e consuntivo.',
				delivered: 'purchaseddts',
				deliveredText: 'ricevuto',
				invoiced: ['purchaseinvoices', 'debitnotes', 'suppliercreditnotes'],
				invoicedText: 'fatturato dai fornitori'
			},
			documents: {},
			activeDocumentTypes: {
				saleorders: { label: 'Ordini', icon: 'mdi-cart' },
				ddts: { label: 'DDT', icon: 'mdi-truck' },
				saleinvoices: { label: 'Fatture', icon: 'mdi-receipt' },
				creditnotes: { label: 'Note di Credito', icon: 'mdi-receipt-text-minus' }
			},
			passiveDocumentTypes: {
				purchaseorders: { label: 'Ordini', icon: 'mdi-cart-minus' },
				purchaseddts: { label: 'DDT', icon: 'mdi-truck-minus' },
				purchaseinvoices: { label: 'Fatture', icon: 'mdi-receipt-text-minus-outline' },
				debitnotes: { label: 'Note di Debito', icon: 'mdi-receipt-text-remove-outline' },
				suppliercreditnotes: { label: 'NC Fornitore', icon: 'mdi-receipt-text-remove-outline' },
				// DDT di vendita di consumo e rientro: costi al costo di riga (rientri in negativo), non ricavi
				consumptions: { label: 'Consumi', icon: 'mdi-package-down' }
			},
			expandedActive: {},
			expandedPassive: {},
			documentProducts: {},
			loadingProducts: {},
			expandedTickets: [],
			ticketInterventions: {},
			loadingInterventions: {},
			selectedActiveDocType: 'saleorders',
			selectedPassiveDocType: 'purchaseorders',
			searchActiveProduct: '',
			searchPassiveProduct: '',
			searchTimeout: null,
			typeDocumentProducts: {
				active: [],
				passive: []
			},
			loadingTypeProducts: {
				active: false,
				passive: false
			},
			typeDocumentProductsTotal: {
				active: 0,
				passive: 0
			},
			typeDocumentProductsAggregates: {
				active: { unique_documents: 0, total_quantity: 0, total_value: 0 },
				passive: { unique_documents: 0, total_quantity: 0, total_value: 0 }
			},
			excludedDocumentHint: 'La causale non conta per la commessa (es. conto visione, riparazione): il documento non entra in ricavi, costi né quadratura',
			documentSummaryHeaders: [
				{ text: 'Tipo', value: 'type', align: 'left' },
				{ text: 'Documenti', value: 'count', align: 'center' },
				{ text: 'Imponibile', value: 'subtotal', align: 'right', type: 'currency' },
				// ✓ se la riga entra nel KPI, altrimenti il motivo (vedi kpiRows)
				{ text: 'Conteggiato', value: 'kpiStatus', align: 'center', sortable: false }
			],
			quotationHeaders: [
				{ text: 'Numero', value: 'number', align: 'left' },
				{ text: 'Nome', value: 'name', align: 'left' },
				{ text: 'Data', value: 'date', align: 'left' },
				{ text: 'Imponibile', value: 'subtotal', align: 'right' }
			],
			typeProductHeaders: [
				{ text: 'Documento', value: 'document_number', align: 'left' },
				{ text: 'Codice', value: 'code', align: 'left' },
				{ text: 'Prodotto', value: 'name', align: 'left' },
				{ text: 'Quantità', value: 'quantity', align: 'center' },
				{ text: 'Prezzo', value: 'price', align: 'right' },
				{ text: 'Imponibile', value: 'total', align: 'right' }
			],
			ticketHeaders: [
				{ text: 'Numero', value: 'number', align: 'left' },
				{ text: 'Titolo', value: 'title', align: 'left' },
				{ text: 'Data', value: 'created_at', align: 'center' },
				{ text: 'Imponibile', value: 'subtotal', align: 'right' },
				{ text: 'Ricavo materiali', value: 'materials_revenue', align: 'right' },
				{ text: 'Costo materiali', value: 'materials_cost', align: 'right' },
				{ text: 'Costo manodopera', value: 'interventions_cost', align: 'right' },
				{ text: '', value: 'data-table-expand' }
			],
			// Quadratura materiali per prodotto (endpoint project-material-reconciliation)
			reconciliation: {
				summary: null,
				rows: [],
				loading: false,
				onlyIssues: true,
				allProducts: false,
				search: ''
			},
			interventionHeaders: [
				{ text: 'Operatore', value: 'operator', align: 'left' },
				{ text: 'Tipo', value: 'type_text', align: 'left' },
				{ text: 'Data Inizio', value: 'start_date', align: 'center' },
				{ text: 'Data Fine', value: 'end_date', align: 'center' },
				{ text: 'Durata', value: 'duration_minutes', align: 'center' },
				{ text: 'Totale', value: 'total', align: 'right' }
			],
			activeProductDocumentPagination: {
				page: 1,
				itemsPerPage: 10,
				sortBy: ['document_number'],
				sortDesc: [],
				multiSort: false
			},
			passiveProductDocumentPagination: {
				page: 1,
				itemsPerPage: 10,
				sortBy: ['document_number'],
				sortDesc: [],
				multiSort: false
			},
			selectedRevenueTypes: [],
			selectedCostTypes: [],
			rulesDialog: false,
			// Regole di conteggio della commessa, mostrate nel dialog di aiuto
			commissionRules: [
				{
					title: 'Da dove nasce il costo del materiale',
					text: 'Dall\'acquisto a commessa (DDT o fattura di acquisto con il progetto: conta il più alto dei due; l\'ordine è l\'ordinato, non ancora un costo), dai Consumi in commessa (DDT con causale di consumo, per il materiale montato senza un DDT al cliente) e, per il materiale preso dal magazzino e uscito con un DDT o un intervento, in automatico come "Materiale da magazzino" al costo delle righe in uscita.'
				},
				{
					title: 'Le causali dei DDT',
					text: 'La causale decide come conta un DDT. Di vendita: vendita (ricavo), reso da cliente (storna il ricavo), consumo e rientro da commessa (costo e suo storno, non fatturabili), senza movimento e non fatturabile (fuori dalla commessa). Di acquisto: acquisto, reso a fornitore (storna il costo), costo commessa senza movimento (noleggi, alberghi).'
				},
				{
					title: 'Da dove nasce il ricavo e l\'uscita del materiale',
					text: 'Dal DDT di vendita (o dalla fattura) e dai materiali inseriti negli interventi dei ticket: sono entrambi vendite al cliente e scaricano il magazzino. Un materiale non va inserito sia nel DDT di vendita sia nell\'intervento: conterebbe due volte come ricavo e come uscita.'
				},
				{
					title: 'Ticket: materiali e manodopera sono ricavi',
					text: 'I materiali degli interventi contano al loro imponibile, la manodopera è l\'imponibile del ticket meno i materiali. Il costo della manodopera è un costo. Se il ticket è stato convertito in DDT ("Converti in DDT"), ricavi e materiali contano nel DDT e del ticket resta solo il costo della manodopera.'
				},
				{
					title: 'Costo del venduto',
					text: 'Costo di acquisto della merce consegnata: righe dei DDT di vendita, delle fatture senza DDT e dei materiali degli interventi. Descrive lo stesso materiale di acquisti, consumi e materiale da magazzino: è una vista alternativa, non va sommato a quelli.'
				},
				{
					title: 'Quadratura materiali',
					text: 'Per ogni prodotto confronta le quantità entrate come costo con quelle uscite verso il cliente, in quantità e al costo. Preso da magazzino (uscite maggiori delle entrate): materiale uscito senza acquisto né consumo sulla commessa, già conteggiato in automatico nei costi come Materiale da magazzino; se non viene dal magazzino è stato inserito due volte. Comprato, da consegnare (entrate maggiori): materiale ancora da consegnare o da rendere. Previsto: quantità degli ordini di vendita.'
				},
				{
					title: 'Materiali task',
					text: 'Stima al listino d\'acquisto corrente dei prodotti inseriti nei task del progetto. Non è un consuntivo.'
				},
				{
					title: 'Ricavi e costi ad oggi',
					text: 'Gli ordini non contano: quelli di vendita sono il previsto, quelli di acquisto l\'ordinato. Per i ricavi conta il più alto tra consegnato (DDT) e fatturato (fatture meno note di credito), così una fattura d\'acconto non abbassa i ricavi; per i costi il più alto tra ricevuto (DDT di acquisto) e fatturato dai fornitori (meno note di debito e NC fornitore). Consumi, costi commessa, materiale da magazzino e manodopera dei ticket si sommano sempre. Con "Personalizza" si scelgono le voci a mano: sommare ordini, DDT e fatture dello stesso flusso conta due volte la stessa merce. Nei riepiloghi la colonna "Conteggiato" mostra ✓ per le righe sommate, altrimenti il motivo.'
				},
				{
					title: 'Previsto, ordinato e costo atteso a fine lavori',
					text: 'Previsto: ricavi dagli ordini di vendita (o dai preventivi accettati), costi dal budget costi del progetto o, se vuoto, dal costo delle righe. Ordinato: ordini di acquisto non ancora arrivati. Costo atteso a fine lavori: con il budget il più alto tra budget e speso + ordinato; senza budget quanto già speso e ordinato più ciò che resta da spendere delle voci previste, così gli extra non previsti lo alzano. Margine atteso a fine lavori = previsto dei ricavi − costo atteso. Le differenze dal previsto si colorano solo a progetto chiuso: prima è normale essere sotto il previsto.'
				},
				{
					title: 'Ordini d\'acquisto e DDT di trasferimento',
					text: 'Non ordinare materiale che si prende dal magazzino: nella generazione degli ordini usare "Da magazzino". Il DDT di trasferimento sposta solo la merce tra magazzini: non è mai un costo.'
				}
			]
		}
	},
	computed: {

		project() {
			return this.models ? this.models.project : null
		},

		company() {
			return this.models ? this.models.company : null
		},

		destination() {
			return this.models ? this.models.destination : null
		},

		contact() {
			return this.models ? this.models.contact : null
		},

		allDocumentTypes() {
			return {
				...this.activeDocumentTypes,
				...this.passiveDocumentTypes,
				troubles: { label: 'Ticket', icon: 'mdi-ticket' }
			}
		},

		filteredActiveDocumentTypes() {
			const filtered = {}
			Object.keys(this.activeDocumentTypes).forEach(type => {
				if (this.getDocumentCount(type) > 0) {
					filtered[type] = this.activeDocumentTypes[type]
				}
			})
			return filtered
		},

		filteredPassiveDocumentTypes() {
			const filtered = {}
			Object.keys(this.passiveDocumentTypes).forEach(type => {
				if (this.getDocumentCount(type) > 0) {
					filtered[type] = this.passiveDocumentTypes[type]
				}
			})
			return filtered
		},

		activeDocumentsList() {
			const types = ['saleorders', 'ddts']
			const list = types
				.filter(type => {
					const count = this.statistics.active_documents[type]?.count || 0
					return count > 0
				})
				.map(type => ({
					type: this.activeDocumentTypes[type].label,
					count: this.statistics.active_documents[type].count,
					subtotal: this.statistics.active_documents[type].subtotal,
					kpi: [type]
				}))

			// Fatture e note di credito in una riga combinata: l'importo lo calcola kpiRows sulle parti sommate nel KPI
			const combined = this.combinedSummaryRow(this.statistics.active_documents, [
				{ value: 'saleinvoices', text: 'Fatture' },
				{ value: 'creditnotes', text: 'Note di Credito', subtract: true }
			])
			if (combined) list.push(combined)

			// Ticket non convertiti in DDT: materiali e manodopera sono vendite al cliente
			const troubles = this.statistics.troubles || {}
			if (troubles.materials_revenue > 0) {
				list.push({ type: 'Materiali ticket', count: troubles.materials_count, subtotal: troubles.materials_revenue, ticket: true, kpi: ['ticket_materials'] })
			}
			if (troubles.labor_revenue > 0) {
				list.push({ type: 'Manodopera ticket', count: troubles.open_count, subtotal: troubles.labor_revenue, ticket: true, kpi: ['ticket_labor'] })
			}

			return list
		},

		passiveDocumentsList() {
			const types = ['purchaseorders', 'purchaseddts']
			const list = types
				.filter(type => {
					const count = this.statistics.passive_documents[type]?.count || 0
					return count > 0
				})
				.map(type => ({
					type: this.passiveDocumentTypes[type].label,
					count: this.statistics.passive_documents[type].count,
					subtotal: this.statistics.passive_documents[type].subtotal,
					kpi: [type]
				}))

			// Fatture d'acquisto, note di debito e note di credito fornitore (entrambe in sottrazione) in una riga combinata
			const combined = this.combinedSummaryRow(this.statistics.passive_documents, [
				{ value: 'purchaseinvoices', text: 'Fatture' },
				{ value: 'debitnotes', text: 'Note di Debito', subtract: true },
				{ value: 'suppliercreditnotes', text: 'NC Fornitore', subtract: true }
			])
			if (combined) list.push(combined)

			// Consumi e costi commessa sono documenti; materiale da magazzino, costo del venduto (vista
			// alternativa) e manodopera dei ticket sono riepiloghi
			const passive = this.statistics.passive_documents
			if (passive.consumptions?.count > 0) {
				list.push({ type: 'Consumi commessa', count: passive.consumptions.count, subtotal: passive.consumptions.subtotal, kpi: ['consumptions'] })
			}
			if (passive.project_costs?.count > 0) {
				list.push({ type: 'Costi commessa', count: passive.project_costs.count, subtotal: passive.project_costs.subtotal, kpi: ['project_costs'] })
			}
			const material = this.statistics.material_costs || {}
			if (material.stock?.subtotal > 0) {
				list.push({ type: 'Materiale da magazzino', count: material.stock.count, subtotal: material.stock.subtotal, summary: true, kpi: ['stock'] })
			}
			if (material.cogs?.subtotal > 0) {
				list.push({ type: 'Costo del venduto', count: material.cogs.count, subtotal: material.cogs.subtotal, summary: true, kpi: ['cogs'] })
			}
			if (this.statistics.troubles?.interventions_cost > 0) {
				list.push({ type: 'Manodopera ticket', count: this.statistics.troubles.count, subtotal: this.statistics.troubles.interventions_cost, summary: true, kpi: ['interventions_cost'] })
			}
			// Stima al listino dei prodotti nei task (count = task con prodotti): vista alternativa, mai nel default
			if (material.task_products?.subtotal > 0) {
				list.push({ type: 'Materiali task (stima)', count: material.task_products.count, subtotal: material.task_products.subtotal, summary: true, kpi: ['task_products'] })
			}

			return list
		},

		// Righe dei riepiloghi con lo stato rispetto alla selezione dei KPI (vedi kpiRows)
		activeSummaryRows() {
			return this.kpiRows(this.activeDocumentsList, this.availableRevenueTypes, this.selectedRevenueTypes, this.revenueFlow)
		},

		passiveSummaryRows() {
			return this.kpiRows(this.passiveDocumentsList, this.availableCostTypes, this.selectedCostTypes, this.costFlow)
		},

		quotationsList() {
			return this.documents.quotations || []
		},

		totalQuotationsAmount() {
			return this.quotationsList.reduce((s, q) => s + (parseFloat(q.subtotal) || 0), 0)
		},

		soldAmount() {
			return parseFloat(this.statistics.active_documents.saleorders?.subtotal) || 0
		},

		quotationConversionPercentage() {
			return this.totalQuotationsAmount > 0
				? ((this.soldAmount / this.totalQuotationsAmount) * 100).toFixed(2)
				: 0
		},

		// Materiali e Manodopera ticket descrivono gli stessi ticket: si contano una volta sola
		totalActiveDocumentsCount() {
			const hasTickets = this.activeDocumentsList.some(doc => doc.ticket)
			const tickets = hasTickets ? (this.statistics.troubles?.open_count || 0) : 0
			return this.sumDocumentCounts(this.activeDocumentsList.filter(doc => !doc.ticket)) + tickets
		},

		// Costo del venduto e Manodopera ticket sono riepiloghi, non documenti passivi
		totalPassiveDocumentsCount() {
			return this.sumDocumentCounts(this.passiveDocumentsList.filter(doc => !doc.summary))
		},

		activeDocumentTypesList() {
			return Object.keys(this.activeDocumentTypes)
				.filter(key => this.getDocumentCount(key) > 0)
				.map(key => ({
					text: this.activeDocumentTypes[key].label,
					value: key
				}))
		},

		passiveDocumentTypesList() {
			return Object.keys(this.passiveDocumentTypes)
				.filter(key => this.getDocumentCount(key) > 0)
				.map(key => ({
					text: this.passiveDocumentTypes[key].label,
					value: key
				}))
		},

		activeDocsCount() {
			return this.typeDocumentProductsAggregates.active.unique_documents || 0
		},

		passiveDocsCount() {
			return this.typeDocumentProductsAggregates.passive.unique_documents || 0
		},

		activeProductsQuantity() {
			const qty = this.typeDocumentProductsAggregates.active.total_quantity || 0
			return parseFloat(qty).toFixed(2)
		},

		passiveProductsQuantity() {
			const qty = this.typeDocumentProductsAggregates.passive.total_quantity || 0
			return parseFloat(qty).toFixed(2)
		},

		activeProductsValue() {
			return this.typeDocumentProductsAggregates.active.total_value || 0
		},

		passiveProductsValue() {
			return this.typeDocumentProductsAggregates.passive.total_value || 0
		},

		// KPI Selection - Available revenue document types (only those with documents)
		availableRevenueTypes() {
			const types = []
			const docs = this.statistics.active_documents

			if (docs.saleorders?.count > 0) {
				types.push({ value: 'saleorders', text: 'Ordini', total: parseFloat(docs.saleorders.subtotal) || 0 })
			}
			if (docs.ddts?.count > 0) {
				types.push({ value: 'ddts', text: 'DDT', total: parseFloat(docs.ddts.subtotal) || 0 })
			}
			if (docs.saleinvoices?.count > 0) {
				types.push({ value: 'saleinvoices', text: 'Fatture', total: parseFloat(docs.saleinvoices.subtotal) || 0 })
			}
			if (docs.creditnotes?.count > 0) {
				types.push({ value: 'creditnotes', text: 'Note di Credito', total: parseFloat(docs.creditnotes.subtotal) || 0, subtract: true })
			}
			const troubles = this.statistics.troubles || {}
			if (troubles.materials_revenue > 0) {
				types.push({ value: 'ticket_materials', text: 'Materiali ticket', total: parseFloat(troubles.materials_revenue) || 0 })
			}
			if (troubles.labor_revenue > 0) {
				types.push({ value: 'ticket_labor', text: 'Manodopera ticket', total: parseFloat(troubles.labor_revenue) || 0 })
			}

			return types
		},

		// KPI Selection - Available cost document types (only those with documents)
		availableCostTypes() {
			const types = []
			const docs = this.statistics.passive_documents

			if (docs.purchaseorders?.count > 0) {
				types.push({ value: 'purchaseorders', text: 'Ordini', total: parseFloat(docs.purchaseorders.subtotal) || 0 })
			}
			if (docs.purchaseddts?.count > 0) {
				types.push({ value: 'purchaseddts', text: 'DDT', total: parseFloat(docs.purchaseddts.subtotal) || 0 })
			}
			if (docs.purchaseinvoices?.count > 0) {
				types.push({ value: 'purchaseinvoices', text: 'Fatture', total: parseFloat(docs.purchaseinvoices.subtotal) || 0 })
			}
			if (docs.debitnotes?.count > 0) {
				types.push({ value: 'debitnotes', text: 'Note di Debito', total: parseFloat(docs.debitnotes.subtotal) || 0, subtract: true })
			}
			if (docs.suppliercreditnotes?.count > 0) {
				types.push({ value: 'suppliercreditnotes', text: 'NC Fornitore', total: parseFloat(docs.suppliercreditnotes.subtotal) || 0, subtract: true })
			}
			if (docs.consumptions?.count > 0) {
				types.push({ value: 'consumptions', text: 'Consumi commessa', total: parseFloat(docs.consumptions.subtotal) || 0 })
			}
			if (docs.project_costs?.count > 0) {
				types.push({ value: 'project_costs', text: 'Costi commessa', total: parseFloat(docs.project_costs.subtotal) || 0 })
			}

			// Materiale da magazzino, costo del venduto (vista alternativa), manodopera dei ticket, stima dei task
			const material = this.statistics.material_costs || {}
			if (material.stock?.subtotal > 0) {
				types.push({ value: 'stock', text: 'Materiale da magazzino', total: parseFloat(material.stock.subtotal) || 0 })
			}
			if (material.cogs?.subtotal > 0) {
				types.push({ value: 'cogs', text: 'Costo del venduto', total: parseFloat(material.cogs.subtotal) || 0 })
			}
			if (this.statistics.troubles?.interventions_cost > 0) {
				types.push({ value: 'interventions_cost', text: 'Manodopera ticket', total: parseFloat(this.statistics.troubles.interventions_cost) || 0 })
			}
			if (material.task_products?.subtotal > 0) {
				types.push({ value: 'task_products', text: 'Materiali task (stima)', total: parseFloat(material.task_products.subtotal) || 0 })
			}

			return types
		},

		// Selezione di default dei ricavi ("Ricavi ad oggi", come forecast.today del BE): il più alto tra consegnato
		// (DDT) e fatturato (fatture meno note di credito), più materiali e manodopera dei ticket non convertiti in
		// DDT. Mai gli ordini: sono il previsto. Un DDT senza prezzi vale 0 e perde il confronto con le fatture
		defaultRevenueTypes() {
			const available = this.availableRevenueTypes.map(type => type.value)
			const selected = this.higherFlowTypes(this.availableRevenueTypes, this.revenueFlow)
			const additive = ['ticket_materials', 'ticket_labor']
			additive.forEach(type => {
				if (available.includes(type)) selected.push(type)
			})
			return selected
		},

		// Selezione di default dei costi ("Costi ad oggi"): il più alto tra ricevuto (DDT di acquisto) e fatturato dai
		// fornitori (fatture meno note di debito e NC fornitore), più le voci additive (consumi, costi commessa,
		// materiale da magazzino, costo manodopera ticket). Mai gli ordini di acquisto, che sono l'ordinato; fuori
		// anche costo del venduto (vista alternativa dello stesso materiale) e materiali task (stima)
		defaultCostTypes() {
			const available = this.availableCostTypes.map(type => type.value)
			const selected = this.higherFlowTypes(this.availableCostTypes, this.costFlow)
			const additive = ['consumptions', 'project_costs', 'stock', 'interventions_cost']
			additive.forEach(type => {
				if (available.includes(type)) selected.push(type)
			})
			return selected
		},

		// La selezione coincide con il calcolo automatico (in qualunque ordine): il KPI non è personalizzato
		revenueIsDefault() {
			return this.sameSelection(this.selectedRevenueTypes, this.defaultRevenueTypes)
		},

		costIsDefault() {
			return this.sameSelection(this.selectedCostTypes, this.defaultCostTypes)
		},

		// Ricavi, costi e margine ad oggi calcolati dal BE (forecast.today; accettato anche al primo livello):
		// coincidono con la colonna "Ad oggi" della card Previsto e consuntivo. Null con un BE che non li espone
		kpiToday() {
			const today = this.statistics.today || (this.statistics.forecast && this.statistics.forecast.today)
			return today && typeof today === 'object' ? today : null
		},

		// KPI in alto: con la selezione automatica il valore del BE (forecast.today), altrimenti la somma delle voci
		// scelte. Senza forecast.today si ripiega sulla somma, che con la selezione automatica dà lo stesso numero
		kpiRevenue() {
			const today = this.kpiToday ? this.toNumber(this.kpiToday.revenue) : null
			return this.revenueIsDefault && today !== null ? today : this.calculatedRevenue
		},

		kpiCost() {
			const today = this.kpiToday ? this.toNumber(this.kpiToday.cost) : null
			return this.costIsDefault && today !== null ? today : this.calculatedCost
		},

		kpiMargin() {
			const today = this.kpiToday ? this.toNumber(this.kpiToday.margin) : null
			return this.revenueIsDefault && this.costIsDefault && today !== null ? today : this.kpiRevenue - this.kpiCost
		},

		// Aiuto del KPI Ricavi ad oggi in modalità automatica, con i due valori confrontati
		revenueAutoHint() {
			const totals = this.flowTotals(this.availableRevenueTypes, this.revenueFlow)
			const currency = this.$options.filters.currency
			return `Conta il più alto tra consegnato (${currency(totals.delivered)}) e fatturato (${currency(totals.invoiced)}), più i ticket. Gli ordini sono il previsto e non entrano.`
		},

		costAutoHint() {
			const totals = this.flowTotals(this.availableCostTypes, this.costFlow)
			const currency = this.$options.filters.currency
			return `Conta il più alto tra ricevuto (${currency(totals.delivered)}) e fatturato dai fornitori (${currency(totals.invoiced)}), più consumi, costi commessa, materiale da magazzino e manodopera dei ticket. Gli ordini di acquisto non entrano.`
		},

		// Avviso quando la selezione personalizzata somma due anelli della stessa catena (doppio conteggio). Il
		// calcolo automatico non li somma mai: l'avviso serve solo alle selezioni personalizzate
		revenueSelectionWarning() {
			if (this.revenueIsDefault) return ''
			const chain = ['saleorders', 'ddts', 'saleinvoices'].filter(type => this.selectedRevenueTypes.includes(type))
			return chain.length > 1 ? 'ordini, DDT e fatture dello stesso flusso, possibile doppio conteggio' : ''
		},

		costSelectionWarning() {
			if (this.costIsDefault) return ''
			const selected = this.selectedCostTypes
			const chain = ['purchaseorders', 'purchaseddts', 'purchaseinvoices'].filter(type => selected.includes(type))
			if (chain.length > 1) return 'ordini, DDT e fatture dello stesso flusso, possibile doppio conteggio'
			if (selected.includes('cogs') && (chain.length > 0 || selected.includes('consumptions') || selected.includes('stock'))) {
				return 'costo del venduto insieme ad acquisti, consumi o materiale da magazzino, possibile doppio conteggio'
			}
			const uncovered = this.reconciliation.summary ? this.reconciliation.summary.uncovered : 0
			if (uncovered > 0 && !selected.includes('stock') && !selected.includes('cogs')) {
				return `${uncovered} prodotti presi da magazzino restano fuori dai costi: aggiungi Materiale da magazzino`
			}
			return ''
		},

		// Progetto chiuso (stato "done" Completato/Fatto o "cancelled" Annullato nei general_types project_status):
		// solo allora le differenze dal previsto si colorano. A commessa in corso essere sotto o sopra il previsto
		// non è ancora un esito, e il rosso a lavori appena iniziati allarma senza motivo
		projectClosed() {
			// il BE lo calcola con la stessa regola (Project::CLOSED_STATUSES): se c'e' vince il suo
			const closed = this.statistics.forecast && this.statistics.forecast.closed
			if (typeof closed === 'boolean') return closed
			const project = (this.projectModels && this.projectModels.project) || this.project || {}
			return ['done', 'cancelled'].includes(project.status)
		},

		// Righe della quadratura filtrate per ricerca e "solo da sistemare"
		reconciliationRows() {
			const search = (this.reconciliation.search || '').toLowerCase()
			return this.reconciliation.rows.filter(row => {
				if (this.reconciliation.onlyIssues && (row.status === 'balanced' || row.status === 'planned')) return false
				if (!search) return true
				return (row.code || '').toLowerCase().includes(search) || (row.name || '').toLowerCase().includes(search)
			})
		},

		reconciliationHeaders() {
			const headers = [
				{ text: 'Codice', value: 'code', align: 'left' },
				{ text: 'Prodotto', value: 'name', align: 'left' },
				{ text: 'Previsto', value: 'planned_quantity', align: 'right' },
				{ text: 'Acquistato', value: 'purchased_quantity', align: 'right' },
				{ text: 'Consumi', value: 'consumption_quantity', align: 'right' },
				{ text: 'Entrate', value: 'in_quantity', align: 'right', class: 'font-weight-bold' },
				{ text: 'DDT vendita', value: 'ddt_quantity', align: 'right' },
				{ text: 'Interventi', value: 'intervention_quantity', align: 'right' }
			]
			if (this.reconciliation.summary && this.reconciliation.summary.includes_task_products) {
				headers.push({ text: 'Task', value: 'task_quantity', align: 'right' })
			}
			headers.push(
				{ text: 'Uscite', value: 'out_quantity', align: 'right', class: 'font-weight-bold' },
				{ text: 'Differenza', value: 'difference', align: 'right' },
				{ text: 'Valore', value: 'difference_value', align: 'right' },
				{ text: 'Stato', value: 'status', align: 'center', sortable: false }
			)
			return headers
		},

		// Righe della card "Previsto e consuntivo" (statistics.forecast, calcolato dal BE): valori, aiuti brevi e
		// colori. Le differenze dal previsto restano neutre finché il progetto non è chiuso (projectClosed); poi rosso
		// se i costi superano il previsto o il margine resta sotto, verde altrimenti. Per i ricavi sempre neutre.
		// Avanzamento dei costi arancione oltre il previsto, rosso solo a progetto chiuso
		forecastRows() {
			const forecast = this.statistics.forecast
			if (!forecast) return []
			const currency = this.$options.filters.currency
			const { revenue, cost, margin } = forecast
			const revenueParts = revenue.actual_parts || {}
			const costParts = cost.actual_parts || {}
			const closed = this.projectClosed
			const sum = parts => {
				const present = parts.filter(([, value]) => value)
				return present.length ? present.map(([text, value]) => `${text} ${currency(value)}`).join(' + ') : 'nessun documento'
			}
			const varianceClass = (row, unfavourable) => {
				if (!closed || row.variance_percentage === null) return ''
				return unfavourable ? 'error--text' : 'success--text'
			}
			const openVarianceHint = 'Il colore compare a progetto chiuso: a lavori in corso è normale essere sotto il previsto.'
			const progressWithCommitted = cost.planned > 0 ? ((cost.actual + cost.committed) / cost.planned) * 100 : null
			const plannedWarning = ['partial', 'unreliable'].includes(cost.planned_reliability) ? cost.planned_reliability : null
			const coverageHint = this.plannedCoverageText(cost)

			return [
				{
					key: 'revenue',
					label: 'Ricavi',
					icon: 'mdi-trending-up',
					color: 'success',
					planned: revenue.planned,
					plannedHint: {
						saleorders: 'Contratto: ordini di vendita della commessa.',
						quotations: 'Contratto: preventivi accettati, perché la commessa non ha ordini di vendita.'
					}[revenue.planned_source] || 'Nessun ordine di vendita né preventivo accettato.',
					committed: null,
					committedHint: '',
					actual: revenue.actual,
					actualHint: 'Il più alto tra consegnato e fatturato, più i ticket. Qui: ' + sum([
						[revenueParts.documents_source === 'saleinvoices' ? 'fatturato' : 'consegnato', revenueParts.documents],
						['materiali ticket', revenueParts.ticket_materials],
						['manodopera ticket', revenueParts.ticket_labor]
					]),
					variance: revenue.variance,
					variancePercentage: revenue.variance_percentage,
					varianceClass: '',
					varianceHint: 'Ad oggi − previsto, sempre neutro: a lavori in corso i ricavi sono normalmente sotto il contratto.',
					progress: revenue.progress,
					progressColor: 'info',
					progressHint: revenue.progress === null ? 'Nessun ricavo previsto.' : 'Ricavi ad oggi sul contratto.'
				},
				{
					key: 'cost',
					label: 'Costi',
					icon: 'mdi-trending-down',
					color: 'error',
					planned: cost.planned,
					plannedHint: ({
						budget: 'Budget costi del progetto.',
						saleorders: 'Costo stimato delle righe degli ordini di vendita: il budget costi del progetto è vuoto.',
						quotations: 'Costo stimato delle righe dei preventivi accettati: il budget costi del progetto è vuoto.'
					}[cost.planned_source] || 'Nessun budget, ordine di vendita né preventivo accettato.') + (coverageHint ? ' ' + coverageHint.charAt(0).toUpperCase() + coverageHint.slice(1) + '.' : ''),
					plannedWarning,
					committed: cost.committed,
					committedHint: `Ordinato ai fornitori, non ancora arrivato (${cost.committed_count} ${cost.committed_count === 1 ? 'ordine' : 'ordini'}).`,
					actual: cost.actual,
					actualHint: 'Il più alto tra ricevuto e fatturato dai fornitori, più consumi, costi commessa, materiale da magazzino e manodopera dei ticket. Qui: ' + sum([
						[costParts.documents_source === 'purchaseinvoices' ? 'fatturato dai fornitori' : 'ricevuto', costParts.documents],
						['consumi', costParts.consumptions],
						['costi commessa', costParts.project_costs],
						['materiale da magazzino', costParts.stock],
						['manodopera ticket', costParts.ticket_labor]
					]),
					variance: cost.variance,
					variancePercentage: cost.variance_percentage,
					varianceClass: varianceClass(cost, cost.variance > 0),
					varianceHint: 'Ad oggi − previsto. ' + (closed ? 'Rosso se i costi hanno superato il previsto.' : openVarianceHint),
					progress: cost.progress,
					progressColor: cost.progress > 100 ? (closed ? 'error' : 'warning') : (closed ? 'success' : 'primary'),
					progressHint: cost.progress === null
						? (cost.planned_reliability === 'unreliable' ? 'Costo previsto non affidabile: avanzamento non calcolabile.' : 'Nessun costo previsto.')
						: `Costi ad oggi sul previsto; contando gli ordini ai fornitori ${this.formatPercentage(progressWithCommitted)}.`
				},
				{
					key: 'margin',
					label: 'Margine',
					icon: 'mdi-currency-eur',
					color: margin.actual >= 0 ? 'success' : 'error',
					planned: margin.planned,
					plannedHint: 'Ricavi previsti − costi previsti.' + (plannedWarning ? ' Il costo previsto è ' + (plannedWarning === 'unreliable' ? 'non affidabile' : 'una stima parziale') + ': il margine previsto è sovrastimato.' : ''),
					plannedWarning,
					committed: null,
					committedHint: '',
					actual: margin.actual,
					actualHint: 'Ricavi ad oggi − costi ad oggi.',
					variance: margin.variance,
					variancePercentage: margin.variance_percentage,
					varianceClass: varianceClass(margin, margin.variance < 0),
					varianceHint: 'Margine ad oggi − previsto. ' + (closed ? 'Rosso se sotto il previsto.' : 'Il colore compare a progetto chiuso.'),
					progress: null,
					progressHint: 'Non previsto per il margine.'
				}
			]
		},

		// Dettaglio di ricavi e costi ad oggi sotto la tabella "Previsto e consuntivo": contratto, consegnato,
		// fatturato, da fatturare, consegnato non fatturato; per i costi ordinato, ricevuto e fatturato dai fornitori.
		// counted: il documento che entra in "Ad oggi" (actual_parts.documents_source). Se il BE non espone le chiavi
		// nuove (forecast.revenue.delivered...) si ricavano dagli stessi riepiloghi dei documenti
		forecastDetails() {
			const forecast = this.statistics.forecast
			if (!forecast || !forecast.revenue || !forecast.cost) return null
			const { revenue, cost } = forecast
			const active = this.statistics.active_documents || {}
			const passive = this.statistics.passive_documents || {}
			const subtotal = summary => (summary ? parseFloat(summary.subtotal) || 0 : 0)
			const pick = (value, fallback) => {
				const number = this.toNumber(value)
				return number === null ? fallback : number
			}
			const planned = pick(revenue.planned, 0)
			const delivered = pick(revenue.delivered, subtotal(active.ddts))
			const invoiced = pick(revenue.invoiced, subtotal(active.saleinvoices) - subtotal(active.creditnotes))
			const received = pick(cost.received, subtotal(passive.purchaseddts))
			const costInvoiced = pick(cost.invoiced, subtotal(passive.purchaseinvoices) - subtotal(passive.debitnotes) - subtotal(passive.suppliercreditnotes))
			const revenueSource = (revenue.actual_parts || {}).documents_source
			const costSource = (cost.actual_parts || {}).documents_source
			const countedHint = text => ` Conta in "Ad oggi": è il più alto tra ${text}.`

			return [
				{
					key: 'revenue',
					label: 'Ricavi',
					icon: 'mdi-trending-up',
					color: 'success',
					entries: [
						{ key: 'planned', label: 'Contratto', value: planned, hint: 'Ricavi previsti: ordini di vendita o, senza ordini, preventivi accettati.' },
						{
							key: 'delivered',
							label: 'Consegnato',
							value: delivered,
							counted: revenueSource === 'ddts',
							hint: 'DDT di vendita valorizzati, al netto dei resi.' + (revenueSource === 'ddts' ? countedHint('consegnato e fatturato') : '')
						},
						{
							key: 'invoiced',
							label: 'Fatturato',
							value: invoiced,
							counted: revenueSource === 'saleinvoices',
							hint: 'Fatture di vendita meno note di credito.' + (revenueSource === 'saleinvoices' ? countedHint('consegnato e fatturato') : '')
						},
						{ key: 'to_invoice', label: 'Da fatturare', value: pick(revenue.to_invoice, Math.max(planned - invoiced, 0)), hint: 'Contratto − fatturato: quanto resta da fatturare al cliente.' },
						{ key: 'delivered_not_invoiced', label: 'Consegnato non fatturato', value: pick(revenue.delivered_not_invoiced, Math.max(delivered - invoiced, 0)), hint: 'Consegnato − fatturato: merce già consegnata e non ancora fatturata.' }
					]
				},
				{
					key: 'cost',
					label: 'Costi',
					icon: 'mdi-trending-down',
					color: 'error',
					entries: [
						{ key: 'committed', label: 'Ordinato', value: pick(cost.committed, 0), hint: 'Ordinato ai fornitori, non ancora arrivato.' },
						{
							key: 'received',
							label: 'Ricevuto',
							value: received,
							counted: costSource === 'purchaseddts',
							hint: 'DDT di acquisto della commessa, al netto dei resi a fornitore.' + (costSource === 'purchaseddts' ? countedHint('ricevuto e fatturato dai fornitori') : '')
						},
						{
							key: 'invoiced',
							label: 'Fatturato dai fornitori',
							value: costInvoiced,
							counted: costSource === 'purchaseinvoices',
							hint: 'Fatture di acquisto meno note di debito e NC fornitore.' + (costSource === 'purchaseinvoices' ? countedHint('ricevuto e fatturato dai fornitori') : '')
						}
					]
				}
			]
		},

		// Preventivi in trattativa della card "Previsto e consuntivo": nota discreta, solo se ce ne sono
		pendingQuotations() {
			const pending = this.statistics.forecast?.revenue?.pending_quotations
			return pending && pending.count > 0 ? pending : null
		},

		// Lettura "a costi" della card "Previsto e consuntivo" (statistics.forecast.progress, calcolato dal BE).
		// Avanzamento = costi ad oggi / costo atteso a fine lavori (ordinato nel buffer della barra); arancione quando
		// il costo atteso supera il previsto (over_planned), rosso solo a progetto chiuso. Fatturato rispetto
		// all'avanzamento: neutro a lavori in corso, colorato a progetto chiuso. Senza costi previsti niente
		// avanzamento né ricavo atteso ("—")
		forecastProgress() {
			const forecast = this.statistics.forecast
			if (!forecast || !forecast.progress) return null
			const currency = this.$options.filters.currency
			const { revenue, cost, margin, progress } = forecast
			const closed = this.projectClosed
			const percentage = progress.percentage
			const difference = progress.billing_difference
			const unreliable = cost.planned_reliability === 'unreliable'
			const estimate = cost.estimate_at_completion
			const overPlanned = this.toNumber(cost.over_planned) || 0

			let billingLabel = unreliable ? 'Costo previsto non affidabile: non calcolabile' : 'Nessun costo previsto: non calcolabile'
			let billingState = null
			if (difference !== null && typeof difference !== 'undefined') {
				if (Math.abs(difference) < 0.005) {
					billingLabel = 'In linea con l\'avanzamento'
					billingState = 'success'
				} else if (difference < 0) {
					billingLabel = `Da fatturare rispetto all'avanzamento ${currency(-difference)}`
					billingState = 'warning'
				} else {
					billingLabel = `Fatturato in anticipo ${currency(difference)}`
					billingState = 'info'
				}
			}

			return {
				percentage,
				unreliable,
				estimate,
				overPlanned,
				overPlannedColor: closed ? 'error' : 'warning',
				committedPercentage: progress.committed_percentage,
				progressColor: overPlanned > 0 ? (closed ? 'error' : 'warning') : 'primary',
				progressHint: percentage === null
					? (unreliable
						? `Non calcolabile: ${this.plannedCoverageText(cost) || 'il costo previsto è 0'}. Imposta il budget costi del progetto.`
						: 'Non calcolabile: nessun costo previsto (budget costi del progetto o costo delle righe).')
					: `Costi ad oggi ${currency(cost.actual)} su costo atteso a fine lavori ${currency(estimate)}. La parte chiara della barra aggiunge l'ordinato ai fornitori (${currency(cost.committed)}).`,
				billingPercentage: progress.billing_percentage,
				billingLabel,
				billingColor: closed && billingState ? billingState : 'info',
				billingTextClass: closed && billingState ? billingState + '--text' : 'text--secondary',
				billingHint: progress.earned_revenue === null
					? 'Si calcola dall\'avanzamento: senza un costo previsto affidabile non è disponibile.'
					: `Con l'avanzamento al ${this.formatPercentage(percentage)} del contratto (${currency(revenue.planned)}) ci si aspetta di aver fatturato ${currency(progress.earned_revenue)}; fatturato ad oggi ${currency(revenue.invoiced !== undefined ? revenue.invoiced : revenue.actual)}.`,
				marginAtCompletion: margin.at_completion,
				marginHint: margin.at_completion === null
					? (unreliable ? 'Non calcolabile: il costo previsto non è affidabile.' : 'Non calcolabile: nessun costo previsto.')
					: `Contratto ${currency(revenue.planned)} − costo atteso a fine lavori ${currency(estimate)}. ` +
						(cost.planned_source === 'budget'
							? 'Con il budget il costo atteso è il più alto tra budget e speso + ordinato.'
							: 'Il costo atteso somma speso, ordinato e quanto resta delle voci previste: gli extra lo alzano.') +
						(cost.planned_reliability === 'partial' ? ' Stima parziale: alcune righe non hanno un costo.' : '')
			}
		},

		// Nota sotto la tabella "Previsto e consuntivo" quando il costo previsto viene da righe senza costo: stima
		// parziale (informativa) o non affidabile (gia' superata dai costi, oppure 0 con ricavi previsti)
		plannedCostNote() {
			const cost = this.statistics.forecast?.cost
			if (!cost || !['partial', 'unreliable'].includes(cost.planned_reliability)) return null
			const coverage = this.plannedCoverageText(cost)
			if (cost.planned_reliability === 'partial') {
				return { unreliable: false, text: `Costo previsto parziale: ${coverage}; il margine previsto è sovrastimato.` }
			}
			const extrapolated = cost.planned_coverage ? cost.planned_coverage.extrapolated : null
			return {
				unreliable: true,
				text: cost.planned > 0 && extrapolated !== null
					? `Costo previsto non affidabile: ${coverage}, e i costi sostenuti e ordinati superano anche la stima riportata a tutto l'imponibile (${this.$options.filters.currency(extrapolated)}). Avanzamento e margine atteso a fine lavori non sono calcolabili.`
					: `Costo previsto non affidabile: ${coverage || 'nessun costo previsto'}. Avanzamento e margine atteso a fine lavori non sono calcolabili.`
			}
		},

		// Il link al budget solo a chi puo' modificare il progetto
		canEditProject() {
			return this.$store.getters.checkRolePermissions('projects', 'edit')
		},

		// Ordini di vendita della commessa mai evasi mentre ci sono gia' DDT di vendita (avviso sotto i KPI)
		unprocessedSaleorders() {
			const warning = this.statistics.warnings && this.statistics.warnings.unprocessed_saleorders
			return warning ? warning.saleorders || [] : []
		},

		// Documenti da creare dalla commessa, con la causale (e il sezionale) proposti dal BE
		newDocumentItems() {
			const documents = this.statistics.new_documents || {}
			const items = [
				{ role: 'consumption', route: 'ddts-create', permission: 'ddts', label: 'Consumo commessa', hint: 'Materiale montato senza DDT al cliente', icon: 'mdi-package-down' },
				{ role: 'consumption_return', route: 'ddts-create', permission: 'ddts', label: 'Rientro da commessa', hint: 'Materiale che torna dal cantiere', icon: 'mdi-package-up' },
				{ role: 'project_cost', route: 'purchaseddts-create', permission: 'purchaseddts', label: 'Costo commessa', hint: 'Noleggi, alberghi, costi senza merce', icon: 'mdi-cash-minus' }
			]
			return items
				.filter(item => documents[item.role] && this.$store.getters.checkRolePermissions(item.permission, 'create'))
				.map(item => ({ ...item, causal: documents[item.role] }))
		},

		// Il BE non propone nessun documento (nessuna causale scelta nelle impostazioni progetti) e l'utente puo' sistemarlo
		canConfigureCommissionCausals() {
			return Object.keys(this.statistics.new_documents || {}).length === 0
				&& this.$store.getters.checkRolePermissions('settings', 'index')
		},

		// Somma delle voci selezionate (righe con ✓ dei riepiloghi); il KPI la mostra se la selezione è personalizzata
		calculatedRevenue() {
			return this.availableRevenueTypes
				.filter(type => this.selectedRevenueTypes.includes(type.value))
				.reduce((sum, type) => {
					return type.subtract ? sum - (type.total || 0) : sum + (type.total || 0)
				}, 0)
		},

		// Calculated cost based on selected types
		calculatedCost() {
			return this.availableCostTypes
				.filter(type => this.selectedCostTypes.includes(type.value))
				.reduce((sum, type) => {
					return type.subtract ? sum - (type.total || 0) : sum + (type.total || 0)
				}, 0)
		},

		// Percentuale del KPI Margine ad oggi sui ricavi del KPI (automatici o personalizzati)
		// null quando non e' significativa: ricavi zero o negativi, oppure oltre ±999% (ricavi minimi rispetto ai costi)
		calculatedMarginPercentage() {
			if (this.kpiRevenue <= 0) return null
			const percentage = (this.kpiMargin / this.kpiRevenue) * 100
			return Math.abs(percentage) > 999 ? null : percentage
		},

		// Total documents count (active + passive)
		totalDocumentsCount() {
			const active = this.statistics.active_documents
			const passive = this.statistics.passive_documents

			const activeCount = (active.saleorders?.count || 0) +
				(active.ddts?.count || 0) +
				(active.saleinvoices?.count || 0) +
				(active.creditnotes?.count || 0)

			const passiveCount = (passive.purchaseorders?.count || 0) +
				(passive.purchaseddts?.count || 0) +
				(passive.purchaseinvoices?.count || 0) +
				(passive.debitnotes?.count || 0) +
				(passive.suppliercreditnotes?.count || 0) +
				(passive.consumptions?.count || 0) +
				(passive.project_costs?.count || 0)

			return activeCount + passiveCount
		}
	},

	created() {
		this.initialize()
	},

	mounted() {
		this.loadTypeDocumentProducts('active')
		this.loadTypeDocumentProducts('passive')
	},

	watch: {
		activeProductDocumentPagination: {
			handler: function(newValue, oldValue) {
				if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
					this.loadTypeDocumentProducts('active')
				}
			},
			deep: true
		},
		passiveProductDocumentPagination: {
			handler: function(newValue, oldValue) {
				if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
					this.loadTypeDocumentProducts('passive')
				}
			},
			deep: true
		},
		activeDocumentTypesList: {
			handler: function(newList) {
				if (newList.length > 0) {
					const currentSelected = this.selectedActiveDocType
					const isCurrentValid = newList.some(item => item.value === currentSelected)
					if (!isCurrentValid) {
						this.selectedActiveDocType = newList[0].value
						this.loadTypeDocumentProducts('active')
					}
				}
			},
			immediate: true
		},
		passiveDocumentTypesList: {
			handler: function(newList) {
				if (newList.length > 0) {
					const currentSelected = this.selectedPassiveDocType
					const isCurrentValid = newList.some(item => item.value === currentSelected)
					if (!isCurrentValid) {
						this.selectedPassiveDocType = newList[0].value
						this.loadTypeDocumentProducts('passive')
					}
				}
			},
			immediate: true
		},
		// Selezione iniziale dei ricavi: il calcolo automatico (vedi defaultRevenueTypes)
		availableRevenueTypes: {
			handler: function(newTypes) {
				if (this.selectedRevenueTypes.length === 0 && newTypes.length > 0) {
					this.selectedRevenueTypes = this.defaultRevenueTypes.slice()
				}
			},
			immediate: true
		},
		// Selezione iniziale dei costi: il calcolo automatico (vedi defaultCostTypes)
		availableCostTypes: {
			handler: function(newTypes) {
				if (this.selectedCostTypes.length === 0 && newTypes.length > 0) {
					this.selectedCostTypes = this.defaultCostTypes.slice()
				}
			},
			immediate: true
		}
	},

	methods: {
		// Consegnato e fatturato di un flusso (revenueFlow/costFlow) dalle voci disponibili: il fatturato e' la prima
		// voce (fatture) meno le note. Le voci senza documenti valgono 0, come nel BE
		flowTotals(availableTypes, flow) {
			const total = value => {
				const type = availableTypes.find(item => item.value === value)
				return type ? (type.subtract ? -type.total : type.total) : 0
			}
			return {
				delivered: total(flow.delivered),
				invoiced: flow.invoiced.reduce((sum, value) => sum + total(value), 0),
				present: availableTypes.some(item => item.value === flow.delivered || flow.invoiced.includes(item.value))
			}
		},

		// Lato del flusso che conta in "ad oggi": il fatturato se e' almeno pari al consegnato, altrimenti il
		// consegnato (regola di Project::commissionForecast). null senza DDT, fatture e note
		higherFlowSide(availableTypes, flow) {
			const totals = this.flowTotals(availableTypes, flow)
			if (!totals.present) return null
			return totals.invoiced >= totals.delivered ? 'invoiced' : 'delivered'
		},

		// Voci disponibili del lato piu' alto (DDT, oppure fatture con le loro note)
		higherFlowTypes(availableTypes, flow) {
			const side = this.higherFlowSide(availableTypes, flow)
			if (!side) return []
			const values = side === 'delivered' ? [flow.delivered] : flow.invoiced
			return values.filter(value => availableTypes.some(item => item.value === value))
		},

		// Stesse voci, in qualunque ordine
		sameSelection(selected, defaults) {
			return selected.length === defaults.length && defaults.every(value => selected.includes(value))
		},

		// Numero da una chiave del BE; null se assente (chiave nuova non ancora esposta) o non numerica
		toNumber(value) {
			if (value === null || typeof value === 'undefined' || value === '') return null
			const number = parseFloat(value)
			return isNaN(number) ? null : number
		},

		// Marca ogni riga del riepilogo in base alle voci KPI che rappresenta (row.kpi), per la colonna "Conteggiato":
		// kpiExcluded se nessuna e' selezionata, kpiReason il motivo (kpiExcludedReason della prima voce),
		// kpiNote le parti di una riga combinata che restano fuori. amount: l'importo mostrato; per le righe
		// combinate solo le parti sommate (tutte se nessuna lo e'), cosi' coincide con quanto entra nel totale
		kpiRows(rows, availableTypes, selectedTypes, flow) {
			return rows.map(row => {
				const types = availableTypes.filter(type => (row.kpi || []).includes(type.value))
				const excluded = types.filter(type => !selectedTypes.includes(type.value))
				const kpiExcluded = excluded.length === types.length
				const first = types.length ? types[0] : null
				const kpiReason = kpiExcluded ? this.kpiExcludedReason(first, availableTypes, selectedTypes, flow) : ''
				const kpiNote = !kpiExcluded && excluded.length > 0 ? `escluse: ${excluded.map(type => type.text).join(', ')}` : ''
				const kpiHint = kpiExcluded ? this.kpiExcludedHint(first, availableTypes, selectedTypes, flow) : ''
				let amount = this.$options.filters.currency(row.subtotal || 0)
				if (row.parts) {
					const included = row.parts.filter(part => selectedTypes.includes(part.value))
					amount = this.partsFormula(included.length ? included : row.parts)
				}
				return { ...row, kpiExcluded, kpiReason, kpiNote, kpiHint, amount }
			})
		},

		// Lato del flusso di una voce: le note (credito, debito, NC fornitore) stanno con le fatture
		flowSide(type, flow) {
			if (type === flow.delivered) return 'delivered'
			if (flow.invoiced.includes(type)) return 'invoiced'
			return null
		},

		// DDT o fatture non sommati mentre e' sommato l'altro lato del flusso: 'higher' se l'altro e' il piu' alto
		// (regola del calcolo automatico), 'included' se l'utente ha scelto il piu' basso (selezione personalizzata:
		// e' la stessa merce, gia' compresa nell'altro documento). 'zero' per i documenti non valorizzati, null altrimenti
		flowExclusion(item, availableTypes, selectedTypes, flow) {
			const side = item ? this.flowSide(item.value, flow) : null
			if (!side) return null
			if (Math.abs(item.total) < 0.005) return 'zero'
			const other = side === 'delivered' ? flow.invoiced : [flow.delivered]
			if (!other.some(value => selectedTypes.includes(value))) return null
			return this.higherFlowSide(availableTypes, flow) !== side ? 'higher' : 'included'
		},

		// Perche' una voce KPI non e' sommata. Ordini: sono il previsto (vendita) o l'ordinato (acquisto). DDT e
		// fatture: vedi flowExclusion. Costo del venduto e materiali task: viste alternative dello stesso materiale
		kpiExcludedReason(item, availableTypes, selectedTypes, flow) {
			const type = item ? item.value : null
			if (type === 'cogs') return 'Stesso materiale, altro calcolo'
			if (type === 'task_products') return 'Già nei costi (stima)'
			if (type === flow.order) return flow.orderReason
			const exclusion = this.flowExclusion(item, availableTypes, selectedTypes, flow)
			if (exclusion === 'zero') return 'Non valorizzato'
			if (exclusion === 'higher') return `Conta il più alto tra ${flow.deliveredText} e fatturato`
			if (exclusion === 'included') return this.flowSide(type, flow) === 'delivered' ? 'Già compreso in fattura' : 'Già compreso nel DDT'
			return 'Non selezionato'
		},

		// Spiegazione breve dei motivi: ordini, il confronto consegnato/fatturato con i due valori, documenti senza
		// prezzi, viste alternative non sommate
		kpiExcludedHint(item, availableTypes, selectedTypes, flow) {
			const type = item ? item.value : null
			const currency = this.$options.filters.currency
			if (type === flow.order) return flow.orderHint
			const exclusion = this.flowExclusion(item, availableTypes, selectedTypes, flow)
			if (exclusion === 'zero') {
				return 'Documenti senza prezzi (es. DDT di sola consegna): non dicono quanto vale la commessa.'
			}
			if (exclusion) {
				const totals = this.flowTotals(availableTypes, flow)
				const values = `${flow.deliveredText.charAt(0).toUpperCase() + flow.deliveredText.slice(1)} ${currency(totals.delivered)}, ${flow.invoicedText} ${currency(totals.invoiced)}`
				return exclusion === 'higher'
					? `${values}: conta il più alto, così ${flow.order === 'saleorders' ? 'un acconto' : 'una fattura parziale del fornitore'} non fa scendere il totale.`
					: `${values}: è la stessa merce, sommarli la conterebbe due volte.`
			}
			if (type === 'cogs') {
				return 'Stesso materiale di acquisti, consumi e magazzino, calcolato su ciò che è stato venduto: sommarlo lo conterebbe due volte.'
			}
			if (type === 'task_products') {
				return 'Stima dei prodotti dei task al listino d\'acquisto: il materiale arriva già nei costi da acquisti, consumi o magazzino.'
			}
			return ''
		},

		// Riga combinata fatture + note in sottrazione, con le sole parti che hanno documenti
		combinedSummaryRow(documents, parts) {
			const present = parts
				.map(part => ({ ...part, count: documents[part.value]?.count || 0, subtotal: parseFloat(documents[part.value]?.subtotal) || 0 }))
				.filter(part => part.count > 0)
			if (!present.length) return null
			return {
				type: present.map(part => part.text).join(' - '),
				count: present.map(part => part.count).join(' + '),
				parts: present,
				kpi: present.map(part => part.value)
			}
		},

		// "A - B = C" sulle parti date (una sola parte: il suo importo, col segno)
		partsFormula(parts) {
			const currency = this.$options.filters.currency
			const net = parts.reduce((sum, part) => sum + (part.subtract ? -part.subtotal : part.subtotal), 0)
			if (parts.length === 1) return currency(net)
			const terms = parts.map((part, index) => {
				if (index === 0) return currency(part.subtract ? -part.subtotal : part.subtotal)
				return `${part.subtract ? '-' : '+'} ${currency(part.subtotal)}`
			})
			return `${terms.join(' ')} = ${currency(net)}`
		},

		summaryRowClass(item) {
			return item.kpiExcluded ? 'text--secondary' : ''
		},

		// Percentuale all'italiana con un decimale ("—" se non calcolabile), col segno se signed
		// Copertura del costo previsto dalle righe (forecast.cost.planned_coverage): "N righe su M senza costo, costo noto
		// sul X% dell'imponibile"; '' con il budget o senza righe
		plannedCoverageText(cost) {
			const coverage = cost && cost.planned_coverage
			if (!coverage || !coverage.rows) return ''
			if (!coverage.rows_without_cost) return 'tutte le righe hanno un costo'
			return `${coverage.rows_without_cost} ${coverage.rows_without_cost === 1 ? 'riga' : 'righe'} su ${coverage.rows} senza costo (costo noto sul ${this.formatPercentage(coverage.percentage)} dell'imponibile)`
		},

		formatPercentage(value, signed = false) {
			if (value === null || typeof value === 'undefined') return '—'
			const options = { maximumFractionDigits: 1 }
			if (signed) options.signDisplay = 'exceptZero'
			return new Intl.NumberFormat('it-IT', options).format(value) + '%'
		},

		// Importo con il segno anche se positivo (scostamenti)
		signedCurrency(value) {
			const amount = this.$options.filters.currency(value || 0)
			return value > 0 ? '+' + amount : amount
		},

		initialize() {
			// Doppio controllo per evitare chiamate duplicate
			if (!this.id || this.loading || this.initialized) {
				console.log('Skipping initialize')
				return
			}

			// Imposta immediatamente i flag per evitare chiamate multiple
			this.loading = true
			this.initialized = true

			this.$store.commit('startProgress')

			this.$store.commit('endProgress')
			Promise.all([
				this.fetchProjectInfo(),
				this.loadStatistics(),
				this.loadDocuments(),
				this.loadReconciliation()
			]).then(() => {

			}).catch(error => {
				console.error('Error loading data:', error)
				// In caso di errore, resetta initialized per permettere retry
			}).finally(() => {
				this.loading = false
			})
		},

		async fetchProjectInfo() {
			try {
				const response = await this.axios.get('projects/' + this.id)
				this.projectModels = response.data.models
				this.infos = response.data.infos
			} catch (error) {
				console.error('Errore caricamento progetto:', error)
			}
		},

		async loadStatistics() {
			try {
				const response = await this.axios.get(`project-commission-statistics/${this.id}`)
				if (response.data.status === 'success') {
					this.statistics = response.data.data
				}
			} catch (error) {
				console.error('Errore caricamento statistiche:', error)
				this.$store.commit('setSnackbarError')
			}
		},

		async loadDocuments() {
			try {
				const response = await this.axios.get(`project-documents/${this.id}`)
				if (response.data.status === 'success') {
					this.documents = response.data.data
				}
			} catch (error) {
				console.error('Errore caricamento documenti:', error)
				this.$store.commit('setSnackbarError')
			}
		},

		async onItemExpanded({ item, value }, type) {
			if (value) {
				await this.loadDocumentProducts(item, type)
			}
		},

		async loadDocumentProducts(document, type) {
			if (!this.loadingProducts[type]) {
				this.$set(this.loadingProducts, type, {})
			}
			this.$set(this.loadingProducts[type], document.id, true)

			try {
				// Passa document_id per ottenere solo i prodotti di questo documento
				const response = await this.axios.get(`project-document-products/${this.id}/${type}`, {
					params: { document_id: document.id }
				})
				if (response.data.status === 'success') {
					// Mappa i prodotti nel formato atteso dal template
					const products = response.data.data.map(item => ({
						id: item.product_id,
						code: item.code,
						name: item.name,
						quantity: item.quantity,
						price: item.price,
						total: item.total
					}))

					if (!this.documentProducts[type]) {
						this.$set(this.documentProducts, type, {})
					}
					this.$set(this.documentProducts[type], document.id, products)
				}
			} catch (error) {
				console.error('Errore caricamento prodotti documento:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.$set(this.loadingProducts[type], document.id, false)
			}
		},

		async loadTypeDocumentProducts(panelType) {
			const documentType = panelType === 'active' ? this.selectedActiveDocType : this.selectedPassiveDocType
			const searchQuery = panelType === 'active' ? this.searchActiveProduct : this.searchPassiveProduct
			const pagination = panelType === 'active' ? this.activeProductDocumentPagination : this.passiveProductDocumentPagination
			this.$set(this.loadingTypeProducts, panelType, true)

			try {
				const params = {
					page: pagination.page,
					per_page: pagination.itemsPerPage
				}
				if (searchQuery && searchQuery.trim() !== '') {
					params.search = searchQuery.trim()
				}
				if (pagination.sortBy && pagination.sortBy.length > 0) {
					params.sortBy = pagination.sortBy[0]
					params.sortDesc = pagination.sortDesc[0] ? '1' : '0'
				}

				const response = await this.axios.get(`project-document-products/${this.id}/${documentType}`, { params })
				if (response.data.status === 'success') {
					this.$set(this.typeDocumentProducts, panelType, response.data.data)
					this.$set(this.typeDocumentProductsTotal, panelType, response.data.total || 0)
					if (response.data.aggregates) {
						this.$set(this.typeDocumentProductsAggregates, panelType, response.data.aggregates)
					}
				}
			} catch (error) {
				console.error('Errore caricamento prodotti per tipo documento:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.$set(this.loadingTypeProducts, panelType, false)
			}
		},

		onSearchActiveProduct() {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(() => {
				this.activeProductDocumentPagination.page = 1
				this.loadTypeDocumentProducts('active')
			}, 300)
		},

		onSearchPassiveProduct() {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(() => {
				this.passiveProductDocumentPagination.page = 1
				this.loadTypeDocumentProducts('passive')
			}, 300)
		},

		onActiveDocTypeChange() {
			this.activeProductDocumentPagination.page = 1
			this.loadTypeDocumentProducts('active')
		},

		onPassiveDocTypeChange() {
			this.passiveProductDocumentPagination.page = 1
			this.loadTypeDocumentProducts('passive')
		},

		getActiveDocumentHeaders() {
			return [
				{ text: 'Numero', value: 'number', align: 'left' },
				{ text: 'Data', value: 'date', align: 'left' },
				{ text: 'Cliente', value: 'company', align: 'left' },
				{ text: 'Imponibile', value: 'subtotal', align: 'right' },
				{ text: '', value: 'data-table-expand' }
			]
		},

		// I consumi sono DDT di vendita: intestati al cliente, non a un fornitore
		getPassiveDocumentHeaders(type) {
			return [
				{ text: 'Numero', value: 'number', align: 'left' },
				{ text: 'Data', value: 'date', align: 'left' },
				{ text: type === 'consumptions' ? 'Cliente' : 'Fornitore', value: 'company', align: 'left' },
				{ text: 'Imponibile', value: 'amount', align: 'right' },
				{ text: '', value: 'data-table-expand' }
			]
		},

		getDocumentCount(type) {
			return this.documents[type] ? this.documents[type].length : 0
		},

		getDocumentHeaders(type) {
			const baseHeaders = [
				{ text: 'Numero', value: 'number', align: 'left' },
				{ text: 'Data', value: 'date', align: 'left' }
			]

			if (type === 'troubles') {
				return [
					{ text: 'Titolo', value: 'title', align: 'left' },
					{ text: 'Stato', value: 'status', align: 'center' },
					{ text: 'Priorità', value: 'priority', align: 'center' },
					{ text: 'Data', value: 'created_at', align: 'left' },
					{ text: 'Totale', value: 'total', align: 'right', type: 'currency' },
					{ text: '', value: 'actions', sortable: false, align: 'center' }
				]
			}

			return [
				...baseHeaders,
				{ text: 'Cliente/Fornitore', value: 'company', align: 'left' },
				{ text: 'Totale', value: 'total', align: 'right', type: 'currency' },
				{ text: '', value: 'actions', sortable: false, align: 'center' }
			]
		},

		getCompanyName(item) {
			if (item.company) return item.company
			if (item.supplier) return item.supplier
			return '-'
		},

		refreshData() {
			if (this.loading) return
			// Reset il tracker per permettere il refresh
			this.initialized = false

			// Ora richiama initialize
			this.initialize()
		},

		getStatusColor(status) {
			const colors = {
				open: 'info',
				closed: 'success',
				pending: 'warning',
				cancelled: 'error'
			}
			return colors[status] || 'grey'
		},

		getProjectStatusColor(status) {
			const colors = {
				active: '#64dd17',
				cancelled: '#b0bec5',
				done: '#00b0ff',
			}
			return colors[status] || 'info'
		},

		documentUrl(type, id) {
			const routes = {
				quotations: 'quotations',
				saleorders: 'orders/saleorders',
				purchaseorders: 'orders/purchaseorders',
				ddts: 'ddts',
				consumptions: 'ddts',
				purchaseddts: 'ddts/purchaseddts',
				saleinvoices: 'invoices/saleinvoices',
				purchaseinvoices: 'invoices/purchaseinvoices',
				creditnotes: 'invoices/creditnotes',
				debitnotes: 'invoices/debitnotes',
				suppliercreditnotes: 'invoices/suppliercreditnotes',
				troubles: 'tickets/troubles'
			}
			return `/${routes[type]}/${id}`
		},

		async loadReconciliation() {
			this.reconciliation.loading = true
			try {
				const response = await this.axios.get(`project-material-reconciliation/${this.id}`, {
					params: { all_products: this.reconciliation.allProducts ? 1 : 0 }
				})
				if (response.data.status === 'success') {
					this.reconciliation.summary = response.data.data.summary
					this.reconciliation.rows = response.data.data.rows
				}
			} catch (error) {
				console.error('Errore caricamento quadratura materiali:', error)
			} finally {
				this.reconciliation.loading = false
			}
		},

		reconciliationStatusColor(status) {
			// Preso da magazzino non è un errore (costo già in Materiale da magazzino): colore informativo distinto da
			// Comprato, da consegnare (warning) e Solo previsto (info)
			return { uncovered: 'teal', surplus: 'warning', balanced: 'success', planned: 'info' }[status] || 'grey'
		},

		reconciliationStatusText(status) {
			// surplus: entrato come costo e non ancora uscito verso il cliente, di solito materiale ancora da consegnare
			return { uncovered: 'Preso da magazzino', surplus: 'Comprato, da consegnare', balanced: 'Quadra', planned: 'Solo previsto' }[status] || status
		},

		sumDocumentCounts(list) {
			return list.reduce((sum, doc) => {
				if (typeof doc.count === 'string') {
					return sum + doc.count.split(' + ').reduce((partial, count) => partial + (parseInt(count) || 0), 0)
				}
				return sum + (doc.count || 0)
			}, 0)
		},

		// DDT con una causale diversa da vendita e acquisto: la commessa lo conta a parte (vedi regole)
		isSpecialCausal(item) {
			return !!item.causal && !!item.commission_role && !['sale', 'purchase'].includes(item.commission_role)
		},

		// DDT con il progetto ma con una causale che la commessa non conta (conto visione, riparazione, conto lavoro):
		// resta nell'elenco per ritrovarlo, senza sembrare un costo o un ricavo
		isExcludedDocument(item) {
			return item.commission_role === 'excluded'
		},

		// Righe prodotto dei documenti fuori commessa: visibili ma attenuate, fuori dai totali (dal BE)
		excludedRowClass(item) {
			return item.commission_excluded ? 'text--disabled' : ''
		},

		causalChipColor(item) {
			return {
				customer_return: 'info',
				supplier_return: 'info',
				consumption: 'error',
				consumption_return: 'info',
				project_cost: 'error',
				excluded: 'grey'
			}[item.commission_role] || 'grey'
		},

		// Nuovo consumo, rientro o costo commessa: causale, sezionale, numero e layout proposti dal BE, progetto e
		// magazzino della commessa, cliente della commessa sui DDT di vendita; al salvataggio si torna alla commessa
		newCommissionDocument(item) {
			const project = this.projectModels && this.projectModels.project ? this.projectModels.project : {}
			const defaultInput = { type: item.causal.type, project_id: this.id }
			if (project.warehouse_id) defaultInput.warehouse_id = project.warehouse_id
			if (item.route === 'ddts-create' && project.company_id) defaultInput.company_id = project.company_id
			if (item.causal.sectional_id) defaultInput.sectional_id = item.causal.sectional_id
			if (item.causal.number) defaultInput.number = item.causal.number
			if (item.causal.layoutgroup_id) defaultInput.layoutgroup_id = item.causal.layoutgroup_id
			this.$router.push({
				name: item.route,
				params: {
					defaultInput,
					redirect: { modelDef: 'projects-commission', id: this.id }
				}
			})
		},

		async onTicketExpanded({ item, value }) {
			if (value) {
				await this.loadTicketInterventions(item)
			}
		},

		async loadTicketInterventions(ticket) {
			this.$set(this.loadingInterventions, ticket.id, true)

			try {
				const response = await this.axios.get(`project-ticket-interventions/${this.id}/${ticket.id}`)
				if (response.data.status === 'success') {
					this.$set(this.ticketInterventions, ticket.id, response.data.data)
				}
			} catch (error) {
				console.error('Errore caricamento interventi ticket:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.$set(this.loadingInterventions, ticket.id, false)
			}
		},

		formatDuration(minutes) {
			if (!minutes) return '-'
			const hours = Math.floor(minutes / 60)
			const mins = minutes % 60
			if (hours > 0) {
				return `${hours}h ${mins}m`
			}
			return `${mins}m`
		}
	}
}
</script>